/**
 * PeopleTab — merged "People ▸ Everyone" surface (X.3.7 Part 2).
 *
 * Replaces the separate Team and Staff tabs with ONE row per person, joining
 * each staff_roster row (Slice A) with its linked user_tenants account. Columns:
 *   - Name           full_name (the real name)
 *   - Role           billable_role (operating role: Care Manager / SPICE / ...)
 *   - Login & access  linked account email + access role in plain words, or
 *                     "No login · Link…"
 *   - Signs as        preferred_name (editable inline)
 *
 * Edge cases are SURFACED, not hidden (the merge's payoff):
 *   - roster row with no account  → "No login · Link…"
 *   - account with no roster row  → a "needs attention" row (link or remove)
 *   - operating role vs access role differing is normal → show both
 *
 * Gated on manage_staff_access OR manage_team (owner + exec_support; server:
 * requireCapability on each route — this per-tab gate is convenience).
 * Reuses the Slice A link/unlink/preferred-name actions plus the Team
 * role-change / deactivate / revoke / bulk-add actions, so nothing from
 * either tab is lost.
 *
 * LIST + DRAWER (founder report 2026-07-25: "there should also be some sort of
 * drawer or list layout and not just vertically stacked because it's long and
 * hard to find people"). Every person used to render their whole editor inline,
 * so the page was one tall stack of forms and a name was hard to land on. Now:
 *
 *   - the page is a SCANNABLE INDEX. One compact row per person carrying only
 *     what you scan by: photo, name, designation, account status. A row never
 *     grows, so the list never reflows under you while you read it.
 *   - the EDITOR lives in the right-side Drawer primitive (the #621 section
 *     anatomy: DrawerSection / DrawerRow on one label grid, pinned footer).
 *     One person open at a time, on a surface wide enough for the bio and the
 *     intro-email preview.
 *   - a SEARCH box appears once the list is long enough to need one
 *     (PEOPLE_SEARCH_MIN); below that it would be chrome for its own sake.
 *
 * Everything #625 established is preserved verbatim: real staff first, dormant
 * login stubs in their own collapsed section at the BOTTOM, and the per-row
 * destructive Remove with its confirm sheet (its self / last-owner / referenced
 * guards are the server's, surfaced through the toast). Login stubs keep their
 * inline affordances — a stub has no roster record, so there is no profile to
 * open, and its two actions ("Link to a person or remove") ARE the row.
 */

import { SignatureBlockField } from '@/components/SignatureBlockField';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronRight, MoreHorizontal, Plus, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canManageStaffProfiles, canManageStaffAccess, canManageTeam } from '@/lib/capabilities';
import {
  staffAdminApi,
  unitOptionLabel,
  type StaffListResponse,
  type StaffProfilesResponse,
  type StaffProfileRow,
  type StaffDirectoryFieldsPatch,
  type HomePointOutcome,
  type StaffCreateOptionsResponse,
  type ArchivePreflightResponse,
  type CreateStaffRequest,
  type UnitOption,
} from '@/lib/staffAdmin';
import { peopleAdminApi, type CreatePersonRequest } from '@/lib/peopleAdmin';
import { ApiError } from '@/lib/api';
import { peopleRefusalText } from '@/lib/peopleRefusals';
import { PHONE_EXAMPLE } from '@/lib/phone';
import { contactSentence, errorCodeOf, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { ErrorBanner } from '@/components/ErrorBanner';
import { preparePhoto, photoUploadErrorMessage } from '@/lib/photoPrep';
import { CmPhoto } from '@/components/workspace/CmPhoto';
import { IntroEmailSection } from '@/components/IntroEmailSection';
import {
  teamApi,
  type TeamListResponse,
  type PendingGrant,
  type BulkAddPreviewResponse,
} from '@/lib/team';
import { ROLE_DISPLAY, TEAM_GRANTABLE_ROLES, displayRole, roleBlurb } from '@/lib/teamRoles';
import { invalidateTeamKeys } from '@/lib/adminReview';
import {
  mergePeople,
  accessRoleLabel,
  billableRoleLabel,
  billableRoleBlurb,
  impliedRatePhrase,
  contactEmail,
  personMatchesQuery,
  BILLABLE_ROLES,
  type BillableRole,
  type PersonRow,
} from '@/lib/people';
import type { UserTenantRole } from '@/lib/types';
import { NON_CLINICAL_STAFF_ROLES } from '@/lib/clientRecordAccess';
import { BottomSheet } from '@/components/primitives/BottomSheet';
import { Button } from '@/components/primitives/Button';
import { Drawer } from '@/components/primitives/Drawer';
import { SearchField } from '@/components/primitives/SearchField';
import {
  DrawerActionRow,
  DrawerRow,
  DrawerRowList,
  DrawerSection,
} from '@/components/workspace/drawer/drawerLayout';
import { DestructiveTextAction, TextAction } from '@/components/workspace/TextAction';
import { FLD, FLD_LABEL } from '@/components/primitives/modalFormStyles';
import {
  AddressCheckChoices,
  AddressCheckPrompt,
  AddressFields,
  PostalAddressDialog,
  useAddressForm,
  type AddressFormParts,
  type PostalSaveOutcome,
} from '@/components/clientRecord/AddressesCard';
import { endSentence } from '@/lib/sentence';
import { PEOPLE_DRAWER_WIDTH_KEY } from '@/lib/keptAcrossSignOut';
import { SelectField } from '@/components/primitives/SelectField';
import { DateInput } from '@/components/primitives/DateInput';

const STALE_MS = 15_000;

/**
 * The list length at which a search box earns its place. Below this a reader
 * takes in the whole list at a glance and a filter is pure chrome; above it,
 * finding a name is the founder's reported problem. One constant, both paths.
 */
const PEOPLE_SEARCH_MIN = 8;

/** The scannable row's chip register: one quiet fact, never a control. */
const ROW_CHIP =
  'inline-flex items-center min-h-5 px-2 py-0.5 text-[11px] font-medium bg-sand text-ink-soft border border-sage-800/[8%] rounded-full';

type SheetState =
  | { kind: 'closed' }
  // The relocated bulk-add door: grants a login (user_tenants membership)
  // for a login with no roster row (a consultant) — the rare case "Add a
  // person" doesn't cover, reached from the login-stubs section at the
  // bottom, not a top-of-page button.
  | { kind: 'bulkAdd' }
  // "+ Add a person" — one door: a staff_roster row and,
  // by default, its login, written together. An actor who can only edit
  // profiles (manage_staff_profiles) gets the same form with the login
  // section read-only and saves through the roster-only route instead.
  | { kind: 'addPerson' }
  | { kind: 'role'; userId: string; name: string; current: UserTenantRole }
  // "Change role" (billable_role) sheet — parallel to the role ('role')
  // access-change sheet but for the operating designation.
  | { kind: 'designation'; rosterId: string; name: string; current: string }
  | { kind: 'active'; userId: string; name: string; makeActive: boolean }
  | { kind: 'revoke'; pending: PendingGrant }
  // "Remove from Olive" archive confirm (sets staff_roster.is_active=false +
  // revokes any linked login).
  | { kind: 'archive'; row: PersonRow }
  // "Remove" confirm for an account_only login stub (DELETE user_tenants —
  // revokes sign-in access for this practice and clears the row).
  | { kind: 'removeLogin'; row: PersonRow }
  // Link picker: from a roster_only row (pick an account) or an account_only
  // row (pick a roster row). `direction` decides which list + which id binds.
  | { kind: 'linkToAccount'; row: PersonRow }
  | { kind: 'linkToRoster'; row: PersonRow }
  // The Directory Editor grant doors' confirm: the person is on the care
  // team of `count` clients and loses access to them. `retry` names the
  // write and its variables; Confirm resubmits it with that count
  // confirmed (data, not a closure, so the sheet holds what was sent).
  | { kind: 'careTeamConfirm'; name: string; count: number; outcome: string; retry: CareTeamRetry };

/** The write a care-team confirm resubmits, with the variables it was sent with. */
type CareTeamRetry =
  | { op: 'link'; vars: { rosterId: string; userId: string; name: string } }
  | { op: 'role'; vars: { userId: string; role: UserTenantRole; name: string } }
  | { op: 'active'; vars: { userId: string; isActive: boolean; name: string } };

/** The number of clients a 409 directory_editor_care_team_confirm asks the
 *  office to confirm, or null for any other failure. */
function careTeamsToConfirm(err: unknown): number | null {
  if (!(err instanceof ApiError) || errorCodeOf(err) !== 'directory_editor_care_team_confirm') return null;
  const n = (err.body as { care_teams?: unknown } | null)?.care_teams;
  return typeof n === 'number' && Number.isInteger(n) && n >= 0 ? n : null;
}

/** Whether an access role is one added as Admin because it does no client
 *  work (the Directory Editor): the designation follows it. */
function isAddedAsAdmin(role: UserTenantRole | ''): boolean {
  return role !== '' && (NON_CLINICAL_STAFF_ROLES as readonly string[]).includes(role);
}

const ADDED_AS_ADMIN_LINE = 'A Directory Editor is added as Admin.';

function careTeamConfirmHeading(count: number): string {
  return count === 1 ? 'Remove access to 1 client record?' : `Remove access to ${count} client records?`;
}

function careTeamConfirmBody(name: string, count: number): string {
  const clients = count === 1 ? '1 client' : `${count} clients`;
  return `${name} is on the care team of ${clients}. As a Directory Editor they lose access to those records. Their care-team history stays.`;
}

/**
 * The care-team confirm of the Directory Editor grant doors, in the SPICE
 * pattern: one line, the door's outcome as the button, and Cancel.
 */
function CareTeamConfirmSheet({
  name,
  count,
  outcome,
  busy,
  onClose,
  onConfirm,
}: {
  name: string;
  count: number;
  outcome: string;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}): ReactNode {
  return (
    <BottomSheet isOpen={true} onClose={onClose} title={careTeamConfirmHeading(count)}>
      <div className="p-3 space-y-3">
        <p className="text-sm text-ink">{careTeamConfirmBody(name, count)}</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="min-h-11 px-4 py-2 text-sm text-ink-soft hover:text-sage-800">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="min-h-11 px-4 py-2 text-sm bg-sage-700 text-cream rounded-md hover:bg-sage-800 disabled:opacity-50"
          >
            {busy ? 'Working…' : outcome}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

async function invalidatePeople(queryClient: QueryClient, tenantSlug: string): Promise<void> {
  // Team mutations also touch the timeline; staff mutations touch the roster.
  await invalidateTeamKeys(queryClient);
  await queryClient.invalidateQueries({ queryKey: ['admin-review', 'staff', tenantSlug] });
  // Slice 3c: the minimal profiles read (non-owner editor) shares the roster.
  await queryClient.invalidateQueries({ queryKey: ['admin-review', 'staff-profiles', tenantSlug] });
}

export function PeopleTab(): ReactNode {
  const { tenantSlug: routeTenantSlug } = useParams<{ tenantSlug: string }>();
  const tenantSlug = routeTenantSlug ?? '';
  const { whoami } = useTenant();
  const queryClient = useQueryClient();
  // Onboard/offboard a person, in one act: add or remove them on the
  // roster, or grant/change/revoke their sign-in and role.
  const hasFullAccess = canManageStaffAccess(whoami) || canManageTeam(whoami);
  // Slice 3c: the broader bio/photo editors (admin_support / data_steward /
  // exec_support). exec_support ALSO has manage_staff_access and takes the
  // full-access path below instead; the other three stay on this one.
  const canEditProfiles = canManageStaffProfiles(whoami);
  // A role turned away from this screen fetches nothing, the tenant
  // config included.
  const contact = useSupportContact({ enabled: hasFullAccess || canEditProfiles });

  // Full-access reads: both carry link status + credentials that the
  // bio/photo-only editors must not see. NEVER widen these to canEditProfiles.
  const staffQuery = useQuery<StaffListResponse, Error>({
    queryKey: ['admin-review', 'staff', tenantSlug],
    queryFn: () => staffAdminApi.list(tenantSlug),
    enabled: hasFullAccess,
    staleTime: STALE_MS,
  });
  const teamQuery = useQuery<TeamListResponse, Error>({
    queryKey: ['admin-review', 'team', tenantSlug],
    queryFn: () => teamApi.list(tenantSlug),
    enabled: hasFullAccess,
    staleTime: STALE_MS,
  });
  // The non-owner editor's MINIMAL read (manage_staff_profiles): no account-
  // link status — since 0161 it carries the profile fields the intro-email
  // block assembles from (email / preferred_name / credentials / phone).
  const profilesQuery = useQuery<StaffProfilesResponse, Error>({
    queryKey: ['admin-review', 'staff-profiles', tenantSlug],
    queryFn: () => staffAdminApi.listProfiles(tenantSlug),
    enabled: !hasFullAccess && canEditProfiles,
    staleTime: STALE_MS,
  });
  // The units catalog for the drawer's unit editor — the SAME query
  // AddPersonSheet fires internally for its own Unit select (identical
  // queryKey, so React Query shares the one fetch/cache entry). Reachable by
  // either People-management path: hasFullAccess or canEditProfiles alone.
  const unitOptionsQuery = useQuery<StaffCreateOptionsResponse, Error>({
    queryKey: ['admin-review', 'staff-create-options', tenantSlug],
    queryFn: () => staffAdminApi.createOptions(tenantSlug),
    enabled: hasFullAccess || canEditProfiles,
    staleTime: STALE_MS,
  });
  const unitOptions: UnitOption[] = unitOptionsQuery.data?.units ?? [];
  // Per-CM cache-bust token for the photo <img>, bumped on a successful
  // upload/remove so a replace shows immediately.
  const [photoVersions, setPhotoVersions] = useState<Record<string, number>>({});
  const bumpPhotoVersion = (rosterId: string): void =>
    setPhotoVersions((m) => ({ ...m, [rosterId]: (m[rosterId] ?? 0) + 1 }));

  const [sheet, setSheet] = useState<SheetState>({ kind: 'closed' });
  const [toast, setToast] = useState<string | null>(null);
  const [showFormer, setShowFormer] = useState(false);
  // Login-only rows (no staff record) live in their own quiet section at the
  // BOTTOM, collapsed by default (founder cleanup 2026-07-25) — unit leads
  // come here for staff bios/photos, not access housekeeping.
  const [showStubs, setShowStubs] = useState(false);
  // The person whose editor drawer is open, held as a KEY rather than a row
  // copy: the row is re-derived from the live list on every render, so a save
  // (bio, photo, designation) shows in the open drawer as soon as the refetch
  // lands instead of stranding a stale snapshot behind the scrim.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // A role-change success, shown inline next to the drawer's role control —
  // the page toast below says the same thing, but the open drawer covers it
  // (the founder's reported bug: feedback that renders behind what's open).
  const [roleChangeNote, setRoleChangeNote] = useState<{ rosterId: string; message: string } | null>(
    null,
  );

  useEffect(() => {
    if (toast === null) return;
    const handle = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(handle);
  }, [toast]);

  // The note belongs to whichever person's drawer was open when it landed;
  // it has nothing to say once that drawer moves on to someone else or closes.
  useEffect(() => {
    setRoleChangeNote(null);
  }, [openKey]);

  const people = useMemo<PersonRow[]>(
    () => mergePeople(staffQuery.data?.rows ?? [], teamQuery.data?.members ?? []),
    [staffQuery.data, teamQuery.data],
  );
  // Default list hides archived ("former staff") roster rows; the toggle reveals
  // them. account_only rows (roster_is_active === null) are always shown.
  const formerCount = useMemo(
    () => people.filter((p) => p.roster_is_active === false).length,
    [people],
  );
  const visiblePeople = useMemo(
    () => (showFormer ? people : people.filter((p) => p.roster_is_active !== false)),
    [people, showFormer],
  );
  // The search box only renders past PEOPLE_SEARCH_MIN, but the filter itself
  // is unconditional so a stale query can never silently hide rows.
  const showSearch = people.length > PEOPLE_SEARCH_MIN;
  const matchedPeople = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length === 0) return visiblePeople;
    return visiblePeople.filter((p) => personMatchesQuery(p, q));
  }, [visiblePeople, search]);
  // Split the list: real staff (linked / roster_only) render in the main
  // table; account_only login stubs go to the collapsed bottom section.
  const staffPeople = useMemo(
    () => matchedPeople.filter((p) => p.kind !== 'account_only'),
    [matchedPeople],
  );
  const stubPeople = useMemo(
    () => matchedPeople.filter((p) => p.kind === 'account_only'),
    [matchedPeople],
  );
  // Re-derived from the FULL list, not the filtered one: typing in the search
  // box (or hiding former staff) must not yank the open drawer out from under
  // the reader. It closes only when the person truly leaves the list.
  const openPerson = useMemo(
    () => (openKey === null ? null : (people.find((p) => p.key === openKey) ?? null)),
    [people, openKey],
  );
  useEffect(() => {
    if (openKey !== null && people.length > 0 && !people.some((p) => p.key === openKey)) {
      setOpenKey(null);
    }
  }, [people, openKey]);
  const pending = teamQuery.data?.pending ?? [];
  // Emails with a queued invite — a roster row showing "No login" whose
  // own email is in here links itself the moment that invite is used
  // (auth_resolve_request's roster auto-link step), not on an operator's
  // manual Link action.
  const pendingEmails = useMemo(
    () => new Set(pending.map((p) => p.email.trim().toLowerCase())),
    [pending],
  );
  const unlinkedRoster = useMemo(
    () => (staffQuery.data?.rows ?? []).filter((r) => !r.linked_user_id),
    [staffQuery.data],
  );
  // Accounts not already bound to a roster row (the only valid link targets;
  // linked_user_id is effectively 1:1, so never offer an already-linked login).
  const unlinkedMembers = useMemo(() => {
    const linkedUserIds = new Set(
      (staffQuery.data?.rows ?? []).flatMap((r) => (r.linked_user_id ? [r.linked_user_id] : [])),
    );
    return (teamQuery.data?.members ?? []).filter((m) => !linkedUserIds.has(m.user_id));
  }, [staffQuery.data, teamQuery.data]);

  const onError = (err: unknown): void => {
    setToast(peopleRefusalText(err, contact));
  };
  const onOk = async (message: string): Promise<void> => {
    setToast(message);
    setSheet({ kind: 'closed' });
    await invalidatePeople(queryClient, tenantSlug);
  };

  // ----- Mutations (Slice A staff + Team) ------------------------------
  const linkMutation = useMutation({
    mutationFn: (input: { rosterId: string; userId: string; name: string; endCareTeams?: number }) =>
      staffAdminApi.link(tenantSlug, input.rosterId, input.userId, input.endCareTeams),
    onSuccess: () => onOk('Linked.'),
    onError: (err, v) => {
      const count = careTeamsToConfirm(err);
      if (count === null) return onError(err);
      setSheet({
        kind: 'careTeamConfirm',
        name: v.name,
        count,
        outcome: 'Link',
        retry: { op: 'link', vars: { rosterId: v.rosterId, userId: v.userId, name: v.name } },
      });
    },
  });
  const unlinkMutation = useMutation({
    mutationFn: (rosterId: string) => staffAdminApi.unlink(tenantSlug, rosterId),
    onSuccess: () => onOk('Unlinked.'),
    onError,
  });
  const preferredNameMutation = useMutation({
    mutationFn: (input: { rosterId: string; name: string }) =>
      staffAdminApi.setPreferredName(tenantSlug, input.rosterId, input.name),
    onSuccess: () => onOk('Saved.'),
    onError,
  });
  const credentialsMutation = useMutation({
    mutationFn: (input: { rosterId: string; credentials: string }) =>
      staffAdminApi.setCredentials(tenantSlug, input.rosterId, input.credentials),
    onSuccess: () => onOk('Saved.'),
    onError,
  });
  // Editable designation (billable_role). The server derives the implied rate
  // flags on a real change, so the refetch shows the "Can log these rates:"
  // switches updated to match.
  const designationMutation = useMutation({
    mutationFn: (input: { rosterId: string; role: BillableRole; name: string }) =>
      staffAdminApi.setBillableRole(tenantSlug, input.rosterId, input.role),
    onSuccess: (_, v) => {
      // Said twice, deliberately: onOk's page toast fires as every other
      // save's does, but it renders behind the open sheet and drawer. This
      // note is what the operator actually sees, right by the control.
      setRoleChangeNote({ rosterId: v.rosterId, message: `Role changed to ${billableRoleLabel(v.role)}.` });
      void onOk(`Changed ${v.name}'s role to ${billableRoleLabel(v.role)}.`);
    },
    // No onError: a refusal renders INLINE inside DesignationChangeSheet, off
    // this mutation's own error — the generic toast onError sets would be
    // just as hidden behind the open sheet and drawer as onOk's is.
  });
  // ----- Slice 3c — bio + photo (manage_staff_profiles) ----------------
  const bioMutation = useMutation({
    mutationFn: (input: { rosterId: string; bio: string }) =>
      staffAdminApi.setBio(tenantSlug, input.rosterId, input.bio),
    onSuccess: () => onOk('Bio saved.'),
    onError,
  });
  // 0161 — the CM direct phone (intro-email contact line). Same autosave
  // discipline as the bio; empty clears to NULL.
  const phoneMutation = useMutation({
    mutationFn: (input: { rosterId: string; phone: string }) =>
      staffAdminApi.setPhone(tenantSlug, input.rosterId, input.phone),
    onSuccess: () => onOk('Phone saved.'),
    onError,
  });
  // 0176 — the directory fields (birthday, anniversary, start date, and the
  // private pair for the office seats): one partial patch per save.
  const directoryMutation = useMutation({
    mutationFn: (input: { rosterId: string; patch: StaffDirectoryFieldsPatch }) =>
      staffAdminApi.setDirectoryFields(tenantSlug, input.rosterId, input.patch),
    onSuccess: (result) => onOk(homePointSavedMessage(result.home_point) ?? 'Directory details saved.'),
    onError,
  });
  // The contracted weekly-hours target the capacity board measures worked
  // hours against. Same autosave discipline as the phone; empty clears.
  const targetHoursMutation = useMutation({
    mutationFn: (input: { rosterId: string; targetWeeklyHours: string }) =>
      staffAdminApi.setTargetWeeklyHours(tenantSlug, input.rosterId, input.targetWeeklyHours),
    onSuccess: () => onOk('Target hours saved.'),
    onError,
  });
  // 0134 — the per-staff SPICE flag (rate-band matrix). Toggling changes
  // which rate bands this person may pick when logging billable time.
  const doesSpiceMutation = useMutation({
    mutationFn: (input: { rosterId: string; doesSpice: boolean }) =>
      staffAdminApi.setDoesSpice(tenantSlug, input.rosterId, input.doesSpice),
    onSuccess: (_, v) =>
      onOk(
        v.doesSpice
          ? 'Saved. They can now log the SPICE rate bands.'
          : 'Saved. The SPICE rate bands are off for them.',
      ),
    onError,
  });
  // 0143 — the per-staff care-management flag (rate-band matrix), the SPICE
  // flag's twin. Toggling changes which rate bands this person may pick when
  // logging billable time.
  const doesCareManagementMutation = useMutation({
    mutationFn: (input: { rosterId: string; doesCareManagement: boolean }) =>
      staffAdminApi.setDoesCareManagement(tenantSlug, input.rosterId, input.doesCareManagement),
    onSuccess: (_, v) =>
      onOk(
        v.doesCareManagement
          ? 'Saved. They can now log the care-management rate bands.'
          : 'Saved. The care-management rate bands are off for them.',
      ),
    onError,
  });
  // Roster/team-grouping unit — any active unit is offered regardless of
  // kind or this person's designation. Refetches the units-backed reads
  // (both people lists) so the Team roster's "No unit yet" group and this
  // page's own display stay in sync.
  const unitMutation = useMutation({
    mutationFn: (input: { rosterId: string; unitId: string | null }) =>
      staffAdminApi.setUnit(tenantSlug, input.rosterId, input.unitId),
    onSuccess: () => onOk('Unit saved.'),
    onError,
  });
  const photoUploadMutation = useMutation({
    mutationFn: (input: { rosterId: string; data: string; mime: string }) =>
      staffAdminApi.uploadPhoto(tenantSlug, input.rosterId, input.data, input.mime),
    onSuccess: (_, v) => {
      bumpPhotoVersion(v.rosterId);
      void onOk('Photo updated.');
    },
    onError,
  });
  const photoDeleteMutation = useMutation({
    mutationFn: (rosterId: string) => staffAdminApi.deletePhoto(tenantSlug, rosterId),
    onSuccess: (_, rosterId) => {
      bumpPhotoVersion(rosterId);
      void onOk('Photo removed.');
    },
    onError,
  });
  const profileBusy = (rosterId: string | null): boolean =>
    rosterId !== null &&
    ((bioMutation.isPending && bioMutation.variables?.rosterId === rosterId) ||
      (phoneMutation.isPending && phoneMutation.variables?.rosterId === rosterId) ||
      (directoryMutation.isPending && directoryMutation.variables?.rosterId === rosterId) ||
      (targetHoursMutation.isPending && targetHoursMutation.variables?.rosterId === rosterId) ||
      (doesSpiceMutation.isPending && doesSpiceMutation.variables?.rosterId === rosterId) ||
      (doesCareManagementMutation.isPending &&
        doesCareManagementMutation.variables?.rosterId === rosterId) ||
      (unitMutation.isPending && unitMutation.variables?.rosterId === rosterId) ||
      (photoUploadMutation.isPending && photoUploadMutation.variables?.rosterId === rosterId) ||
      (photoDeleteMutation.isPending && photoDeleteMutation.variables === rosterId));
  const roleMutation = useMutation({
    mutationFn: (input: { userId: string; role: UserTenantRole; name: string; endCareTeams?: number }) =>
      teamApi.updateRole(tenantSlug, input.userId, input.role, input.endCareTeams),
    onSuccess: (_, v) => onOk(`Changed ${v.name}'s access to ${accessRoleLabel(v.role)}.`),
    onError: (err, v) => {
      const count = careTeamsToConfirm(err);
      if (count === null) return onError(err);
      setSheet({
        kind: 'careTeamConfirm',
        name: v.name,
        count,
        outcome: 'Change access',
        retry: { op: 'role', vars: { userId: v.userId, role: v.role, name: v.name } },
      });
    },
  });
  const activeMutation = useMutation({
    mutationFn: (input: { userId: string; isActive: boolean; name: string; endCareTeams?: number }) =>
      teamApi.updateActive(tenantSlug, input.userId, input.isActive, input.endCareTeams),
    onSuccess: (_, v) => onOk(v.isActive ? endSentence(`Reactivated ${v.name}`) : `Removed ${v.name}'s access.`),
    onError: (err, v) => {
      const count = careTeamsToConfirm(err);
      if (count === null) return onError(err);
      setSheet({
        kind: 'careTeamConfirm',
        name: v.name,
        count,
        outcome: 'Reactivate',
        retry: { op: 'active', vars: { userId: v.userId, isActive: v.isActive, name: v.name } },
      });
    },
  });
  const revokeMutation = useMutation({
    mutationFn: (email: string) => teamApi.revokePending(tenantSlug, email),
    onSuccess: (_, email) => onOk(`Removed the login waiting for ${email}.`),
    onError,
  });
  // "Remove" for an account_only login stub — DELETE user_tenants. The server
  // refuses self-removal, the last active owner, roster-linked logins, and
  // memberships referenced by in-tenant records; those errors surface via the
  // toast (onError shows the server's message).
  const removeLoginMutation = useMutation({
    mutationFn: (input: { userId: string; name: string }) =>
      teamApi.remove(tenantSlug, input.userId),
    onSuccess: (_, v) => onOk(`Removed ${v.name}'s login.`),
    onError,
  });
  const archiveMutation = useMutation({
    mutationFn: (input: { rosterId: string; name: string }) =>
      staffAdminApi.archive(tenantSlug, input.rosterId),
    onSuccess: (_, v) => onOk(`Removed ${v.name} from the list.`),
    onError,
  });
  const restoreMutation = useMutation({
    mutationFn: (input: { rosterId: string; name: string }) =>
      staffAdminApi.restore(tenantSlug, input.rosterId),
    onSuccess: (_, v) => onOk(endSentence(`Restored ${v.name}`)),
    onError,
  });

  const rowBusy = (row: PersonRow): boolean =>
    (linkMutation.isPending && linkMutation.variables?.rosterId === row.roster_id) ||
    (unlinkMutation.isPending && unlinkMutation.variables === row.roster_id) ||
    (preferredNameMutation.isPending && preferredNameMutation.variables?.rosterId === row.roster_id) ||
    (credentialsMutation.isPending && credentialsMutation.variables?.rosterId === row.roster_id) ||
    (designationMutation.isPending && designationMutation.variables?.rosterId === row.roster_id) ||
    (activeMutation.isPending && activeMutation.variables?.userId === row.user_id) ||
    (removeLoginMutation.isPending && removeLoginMutation.variables?.userId === row.user_id) ||
    (roleMutation.isPending && roleMutation.variables?.userId === row.user_id) ||
    (archiveMutation.isPending && archiveMutation.variables?.rosterId === row.roster_id) ||
    (restoreMutation.isPending && restoreMutation.variables?.rosterId === row.roster_id) ||
    profileBusy(row.roster_id);

  // The role and access controls disable on their OWN pending save only —
  // rowBusy's broader "something on this row is in flight" reading is right
  // for the row's other actions, but an unrelated hung save (a photo upload,
  // a bio autosave) must never grey out a control it has nothing to do with.
  const designationBusy = (rosterId: string | null): boolean =>
    rosterId !== null &&
    designationMutation.isPending &&
    designationMutation.variables?.rosterId === rosterId;
  const accessBusy = (userId: string | null): boolean =>
    userId !== null && roleMutation.isPending && roleMutation.variables?.userId === userId;

  // Two-path gate, on CAPABILITY not role: the full roster/access view vs
  // the broader bio/photo-only editor. Anyone with neither cap is denied.
  // (All hooks above run unconditionally — the React #310 rule.)
  if (!hasFullAccess && !canEditProfiles) {
    return (
      <div role="alert" className="my-3 p-3 border border-warm-300 bg-warm-50 rounded-md text-sm text-ink">
        {contactSentence("Managing people is for owners. Ask {contact_admin} if you need to change someone's access.", contact)}
      </div>
    );
  }
  if (!hasFullAccess) {
    // The bio/photo-only editor (admin_support / data_steward /
    // supervising_cm): the minimal surface, fed by the manage_staff_profiles
    // GET /staff. NONE of the full-access controls (link/role/credentials/
    // bulk-add) render. exec_support does NOT land here — manage_staff_access
    // takes it down the full-access path above instead. This actor DOES get
    // "+ Add a person", the one door: the same form, its login
    // section read-only, saving through the roster-only route.
    return (
      <>
        <StaffProfileList
          tenantSlug={tenantSlug}
          profiles={profilesQuery.data?.profiles ?? []}
          isLoading={profilesQuery.isLoading}
          error={profilesQuery.error}
          onRetryProfiles={() => void profilesQuery.refetch()}
          toast={toast}
          photoVersions={photoVersions}
          profileBusy={profileBusy}
          privateContactVisible={profilesQuery.data?.private_contact_visible === true}
          onAddPerson={() => setSheet({ kind: 'addPerson' })}
          onSaveBio={(rosterId, bio) => bioMutation.mutate({ rosterId, bio })}
          onSavePhone={(rosterId, phone) => phoneMutation.mutate({ rosterId, phone })}
          unitOptions={unitOptions}
          onSaveUnit={(rosterId, unitId) => unitMutation.mutate({ rosterId, unitId })}
          onSaveDirectory={(rosterId, patch) => directoryMutation.mutate({ rosterId, patch })}
          onSaveDirectoryHome={async (rosterId, parts) =>
            homeSaveOutcome(await directoryMutation.mutateAsync({ rosterId, patch: { home_address: parts } }))
          }
          onSaveTargetWeeklyHours={(rosterId, targetWeeklyHours) => targetHoursMutation.mutate({ rosterId, targetWeeklyHours })}
          onToggleDoesCareManagement={(rosterId, doesCareManagement) =>
            doesCareManagementMutation.mutate({ rosterId, doesCareManagement })
          }
          onToggleDoesSpice={(rosterId, doesSpice) => doesSpiceMutation.mutate({ rosterId, doesSpice })}
          onUploadPhoto={async (rosterId, data, mime) => {
            await photoUploadMutation.mutateAsync({ rosterId, data, mime });
          }}
          onRemovePhoto={(rosterId) => photoDeleteMutation.mutate(rosterId)}
        />
        <AddPersonSheet
          isOpen={sheet.kind === 'addPerson'}
          tenantSlug={tenantSlug}
          canGrantLogin={false}
          showCompanyControl={false}
          privateContactVisible={profilesQuery.data?.private_contact_visible === true}
          onClose={() => setSheet({ kind: 'closed' })}
          onSuccess={onOk}
        />
      </>
    );
  }

  const isLoading = staffQuery.isLoading || teamQuery.isLoading;
  const error = staffQuery.error ?? teamQuery.error;

  // Shared row renderer — the main staff table and the collapsed
  // "Logins without a staff record" section render identical rows.
  const renderPersonRow = (row: PersonRow): ReactNode => (
    <PersonRowView
      key={row.key}
      row={row}
      busy={rowBusy(row)}
      tenantSlug={tenantSlug}
      photoVersion={row.roster_id ? photoVersions[row.roster_id] : undefined}
      onOpen={() => setOpenKey(row.key)}
      onOpenRosterPicker={() => setSheet({ kind: 'linkToRoster', row })}
      onChangeRole={() =>
        row.user_id &&
        row.access_role &&
        setSheet({
          kind: 'role',
          userId: row.user_id,
          name: personName(row),
          current: row.access_role,
        })
      }
      onChangeDesignation={() =>
        row.roster_id &&
        setSheet({
          kind: 'designation',
          rosterId: row.roster_id,
          name: personName(row),
          current: row.billable_role ?? '',
        })
      }
      onSetActive={(makeActive) =>
        row.user_id &&
        setSheet({ kind: 'active', userId: row.user_id, name: personName(row), makeActive })
      }
      onArchive={() => setSheet({ kind: 'archive', row })}
      onRestore={() =>
        row.roster_id &&
        restoreMutation.mutate({ rosterId: row.roster_id, name: personName(row) })
      }
      onRemoveLogin={() => setSheet({ kind: 'removeLogin', row })}
    />
  );

  return (
    <div>
      {toast && (
        <div
          role="status"
          aria-live="polite"
          className="mb-3 px-3 py-2 text-sm bg-sage-50 border border-sage-200 text-sage-800 rounded-md"
          data-testid="people-toast"
        >
          {toast}
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-sm font-semibold text-ink">Access &amp; Information</h3>
          {/* The live count, from the rows on screen: never a number quoted
              from a document. Every active roster row the table below
              renders, with how many of them are care managers held quietly
              alongside. */}
          <span className="text-xs text-ink-soft" data-testid="people-cm-count">
            {peopleCountLabel(
              (staffQuery.data?.rows ?? []).filter((r) => r.is_active).length,
              (staffQuery.data?.rows ?? []).filter(
                (r) => r.is_active && r.billable_role === 'care_manager',
              ).length,
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* One door: add the person, their role, and — by
              default — their login, in a single save. */}
          <button
            type="button"
            onClick={() => setSheet({ kind: 'addPerson' })}
            data-testid="add-person-open"
            className="min-h-9 px-3 py-1.5 text-xs font-semibold text-sage-700 border border-sage-300 rounded-md hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          >
            + Add a person
          </button>
        </div>
      </div>

      {showSearch && (
        <div className="mb-3">
          <label className="sr-only" htmlFor="people-search">
            Find a person
          </label>
          <SearchField
            id="people-search"
            value={search}
            onChange={setSearch}
            placeholder="Find a person"
            testId="people-search"
          />
        </div>
      )}

      {formerCount > 0 && (
        <label className="flex items-center gap-2 mb-3 text-xs text-ink-soft cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showFormer}
            onChange={(e) => setShowFormer(e.target.checked)}
            className="text-sage-600 focus-visible:ring-sage-400"
          />
          Show former staff ({formerCount})
        </label>
      )}

      {isLoading && <div className="text-sm text-ink-soft py-6 text-center">Loading…</div>}
      {error && (
        <ErrorBanner
          error={error}
          what="the people list"
          onRetry={() => {
            void staffQuery.refetch();
            void teamQuery.refetch();
          }}
          data-testid="people-load-error"
        />
      )}

      {!isLoading && !error && (
        <div
          className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 divide-y divide-sage-800/[8%]"
          data-testid="people-table"
        >
          {staffPeople.length === 0 && pending.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-ink-soft">
              {search.trim().length > 0 ? 'Try another spelling.' : 'Add the first person.'}
            </div>
          )}
          {staffPeople.map(renderPersonRow)}

          {pending.map((p) => (
            <PendingRow key={p.email} pending={p} onRevoke={() => setSheet({ kind: 'revoke', pending: p })} />
          ))}
        </div>
      )}

      {/* Logins without a staff record — the account_only stubs, tucked at
          the BOTTOM under a quiet collapsed toggle (Show-former-staff idiom).
          Needs-attention chips stay on the rows inside; each row carries the
          link + Remove affordances. The relocated "Give sign-in access…" text
          action lives HERE, not a top-of-page button: the rare case it covers
          — a login with no roster row, e.g. a consultant — is exactly what
          this section is for, so the section renders even with zero stubs to
          keep the action reachable. */}
      {!isLoading && !error && (
        <div className="mt-4">
          <div className="flex items-center gap-3 mb-2">
            {stubPeople.length > 0 ? (
              <label className="flex items-center gap-2 text-xs text-ink-soft cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showStubs}
                  onChange={(e) => setShowStubs(e.target.checked)}
                  className="text-sage-600 focus-visible:ring-sage-400"
                />
                Logins without a staff record ({stubPeople.length})
              </label>
            ) : (
              <span className="text-xs text-ink-soft">Logins without a staff record</span>
            )}
            <TextAction onClick={() => setSheet({ kind: 'bulkAdd' })}>
              Give sign-in access…
            </TextAction>
          </div>
          {showStubs && stubPeople.length > 0 && (
            <div
              className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 divide-y divide-sage-800/[8%]"
              data-testid="people-stub-table"
            >
              {stubPeople.map(renderPersonRow)}
            </div>
          )}
        </div>
      )}

      {/* Sheets */}
      <BulkAddSheet
        isOpen={sheet.kind === 'bulkAdd'}
        tenantSlug={tenantSlug}
        onClose={() => setSheet({ kind: 'closed' })}
        onSuccess={onOk}
      />

      <AddPersonSheet
        isOpen={sheet.kind === 'addPerson'}
        tenantSlug={tenantSlug}
        canGrantLogin
        // The company control mirrors the SERVER's field-level gate
        // (ctx.role === 'owner', repos/staff.ts insertStaffRosterRow):
        // absent for everyone else, not disabled. This is DELIBERATELY
        // still a role check, not canManageTeam/canManageStaffAccess —
        // both capabilities are runtime-widenable to a non-owner
        // (ADR-0046), but the company_id field stays owner-only regardless
        // of who else can reach this sheet, so the literal role check is
        // what survives that widening. Do not fold this into hasFullAccess.
        showCompanyControl={whoami?.role === 'owner'}
        privateContactVisible={staffQuery.data?.private_contact_visible === true}
        onClose={() => setSheet({ kind: 'closed' })}
        onSuccess={onOk}
      />

      {sheet.kind === 'role' && (
        <RoleChangeSheet
          name={sheet.name}
          current={sheet.current}
          busy={roleMutation.isPending}
          onClose={() => setSheet({ kind: 'closed' })}
          onConfirm={(role) => roleMutation.mutate({ userId: sheet.userId, role, name: sheet.name })}
        />
      )}

      {sheet.kind === 'designation' && (
        <DesignationChangeSheet
          name={sheet.name}
          current={sheet.current}
          busy={designationMutation.isPending}
          error={designationMutation.error}
          onClose={() => {
            // Clear the last attempt's failure so it can't bleed into the
            // next time this sheet opens, for this person or another.
            designationMutation.reset();
            setSheet({ kind: 'closed' });
          }}
          onConfirm={(role) =>
            designationMutation.mutate({ rosterId: sheet.rosterId, role, name: sheet.name })
          }
        />
      )}

      {sheet.kind === 'active' && (
        <ConfirmSheet
          title={sheet.makeActive ? `Reactivate ${sheet.name}?` : `Remove ${sheet.name}'s access?`}
          body={
            sheet.makeActive
              ? `They get their previous access back.`
              : `Signing in is turned off until you reactivate them. Every record stays.`
          }
          confirmLabel={sheet.makeActive ? 'Reactivate' : 'Remove access'}
          confirmVariant={sheet.makeActive ? 'sage' : 'danger'}
          busy={activeMutation.isPending}
          onClose={() => setSheet({ kind: 'closed' })}
          onConfirm={() =>
            activeMutation.mutate({ userId: sheet.userId, isActive: sheet.makeActive, name: sheet.name })
          }
        />
      )}

      {sheet.kind === 'archive' && (
        <ArchiveConfirmSheet
          tenantSlug={tenantSlug}
          row={sheet.row}
          hasPendingInvite={
            sheet.row.roster_email !== null &&
            pendingEmails.has(sheet.row.roster_email.trim().toLowerCase())
          }
          busy={archiveMutation.isPending}
          onClose={() => setSheet({ kind: 'closed' })}
          onConfirm={() =>
            sheet.row.roster_id &&
            archiveMutation.mutate({ rosterId: sheet.row.roster_id, name: personName(sheet.row) })
          }
        />
      )}

      {sheet.kind === 'removeLogin' && sheet.row.user_id && (
        <ConfirmSheet
          title={`Remove ${personName(sheet.row)}?`}
          body={
            `This removes ${sheet.row.account_email ?? 'this login'} and revokes their sign-in access ` +
            `for this practice. The login disappears from this list; any records that mention them are kept. ` +
            `They can be given a login again later.`
          }
          confirmLabel="Remove"
          confirmVariant="danger"
          busy={removeLoginMutation.isPending}
          onClose={() => setSheet({ kind: 'closed' })}
          onConfirm={() =>
            sheet.row.user_id &&
            removeLoginMutation.mutate({ userId: sheet.row.user_id, name: personName(sheet.row) })
          }
        />
      )}

      {sheet.kind === 'revoke' && (
        <ConfirmSheet
          title={`Remove the login waiting for ${sheet.pending.email}?`}
          body={`The login is removed before they ever sign in. You can give them one again later.`}
          confirmLabel="Remove"
          confirmVariant="danger"
          busy={revokeMutation.isPending}
          onClose={() => setSheet({ kind: 'closed' })}
          onConfirm={() => revokeMutation.mutate(sheet.pending.email)}
        />
      )}

      {sheet.kind === 'linkToAccount' && (
        <LinkPickerSheet
          title={`Link ${sheet.row.full_name ?? 'this person'} to an account`}
          options={unlinkedMembers.map((m) => ({
            id: m.user_id,
            primary: m.display_name ?? m.email,
            secondary: m.email,
          }))}
          emptyLabel="Every login is linked to a person."
          busy={linkMutation.isPending}
          onClose={() => setSheet({ kind: 'closed' })}
          onPick={(userId) =>
            sheet.row.roster_id && linkMutation.mutate({ rosterId: sheet.row.roster_id, userId, name: personName(sheet.row) })
          }
        />
      )}

      {sheet.kind === 'careTeamConfirm' && (
        <CareTeamConfirmSheet
          name={sheet.name}
          count={sheet.count}
          outcome={sheet.outcome}
          busy={roleMutation.isPending || activeMutation.isPending || linkMutation.isPending}
          onClose={() => setSheet({ kind: 'closed' })}
          onConfirm={() => {
            const { retry, count } = sheet;
            if (retry.op === 'link') linkMutation.mutate({ ...retry.vars, endCareTeams: count });
            else if (retry.op === 'role') roleMutation.mutate({ ...retry.vars, endCareTeams: count });
            else activeMutation.mutate({ ...retry.vars, endCareTeams: count });
          }}
        />
      )}

      {sheet.kind === 'linkToRoster' && (
        <LinkPickerSheet
          title={`Link ${personName(sheet.row)} to a person on the roster`}
          options={unlinkedRoster.map((r) => ({
            id: r.id,
            primary: r.full_name,
            secondary: `${billableRoleLabel(r.billable_role)}${r.email ? ` · ${r.email}` : ''}`,
          }))}
          emptyLabel="Add the person to the roster first, then link their login."
          busy={linkMutation.isPending}
          onClose={() => setSheet({ kind: 'closed' })}
          onPick={(rosterId) =>
            sheet.row.user_id && linkMutation.mutate({ rosterId, userId: sheet.row.user_id, name: personName(sheet.row) })
          }
        />
      )}

      {/* The person editor. One person at a time, on a surface wide enough for
          the bio and the intro-email preview — the vertical stack of always-open
          forms the founder flagged is gone from the list itself. */}
      {openPerson && openPerson.roster_id && (
        <PersonDrawer
          name={personName(openPerson)}
          photo={
            <CmPhoto
              tenantSlug={tenantSlug}
              rosterId={openPerson.roster_id}
              name={personName(openPerson)}
              size="lg"
              hasPhoto={openPerson.has_photo}
              version={photoVersions[openPerson.roster_id]}
            />
          }
          onClose={() => setOpenKey(null)}
          badges={
            <>
              <span className="inline-flex items-center gap-1.5">
                <span className="text-[11px] font-medium text-ink-soft">
                  {billableRoleLabel(openPerson.billable_role)}
                </span>
                <button
                  type="button"
                  disabled={designationBusy(openPerson.roster_id)}
                  onClick={() =>
                    openPerson.roster_id &&
                    setSheet({
                      kind: 'designation',
                      rosterId: openPerson.roster_id,
                      name: personName(openPerson),
                      current: openPerson.billable_role ?? '',
                    })
                  }
                  data-testid={`role-change-${openPerson.roster_id}`}
                  aria-label={`Change role for ${personName(openPerson)} (currently ${billableRoleLabel(openPerson.billable_role)})`}
                  className="inline-flex items-center min-h-8 px-2.5 py-1 text-xs font-semibold text-sage-700 border border-sage-300 rounded-md hover:bg-sage-50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                >
                  Change role
                </button>
              </span>
              {roleChangeNote?.rosterId === openPerson.roster_id && (
                <span className="text-xs text-sage-700" role="status">
                  {roleChangeNote.message}
                </span>
              )}
              {openPerson.roster_is_active === false && (
                <span className={ROW_CHIP}>Former staff</span>
              )}
            </>
          }
        >
          {/* One place for onboarding and offboarding: the
              roster lifecycle (edit, remove/restore) and sign-in access
              (give/change/revoke a login) are two DIFFERENT things, so they
              get two labelled sections rather than one shared "Login and
              access" grab-bag. */}
          <DrawerSection title="Roster" testId="person-drawer-roster">
            <DrawerRowList>
              {/* Discoverability (spec §8.4): the roster lifecycle action at
                  the same level as the person's other facts — not only one
                  click deep in the row's overflow menu. Named for the state
                  it produces (former staff). Editing the roster record
                  itself is the Profile + Directory sections below. */}
              <DrawerActionRow label="Status">
                {openPerson.roster_is_active === false ? (
                  <TextAction
                    disabled={rowBusy(openPerson)}
                    onClick={() =>
                      openPerson.roster_id &&
                      restoreMutation.mutate({
                        rosterId: openPerson.roster_id,
                        name: personName(openPerson),
                      })
                    }
                  >
                    Restore to the roster
                  </TextAction>
                ) : (
                  <>
                    <DestructiveTextAction
                      disabled={rowBusy(openPerson)}
                      onClick={() => setSheet({ kind: 'archive', row: openPerson })}
                    >
                      Remove from Olive…
                    </DestructiveTextAction>
                    <span className="text-xs text-ink-soft">Marks them as former staff.</span>
                  </>
                )}
              </DrawerActionRow>
              {/* A removed roster row does not, on its own, revoke a login —
                  archiving does that automatically, but a row can reach this
                  state some other way (e.g. it was already deactivated).
                  Surface it rather than let "former staff" quietly keep a
                  working login. */}
              {openPerson.roster_is_active === false && openPerson.account_is_active === true && (
                <div
                  className="flex items-center justify-between gap-3 -mx-4 mt-1 px-4 py-2 bg-gold-soft/30"
                  data-testid="person-drawer-still-has-access"
                >
                  <span className="text-xs text-caution-deep">Still has sign-in access</span>
                  <DestructiveTextAction
                    disabled={rowBusy(openPerson)}
                    onClick={() =>
                      openPerson.user_id &&
                      setSheet({
                        kind: 'active',
                        userId: openPerson.user_id,
                        name: personName(openPerson),
                        makeActive: false,
                      })
                    }
                  >
                    Revoke
                  </DestructiveTextAction>
                </div>
              )}
            </DrawerRowList>
          </DrawerSection>

          {/* Standing setup rather than the work → the DL4 tint zone. */}
          <DrawerSection title="Sign-in access" tone="tint" testId="person-drawer-access">
            <DrawerRowList>
              <DrawerRow label="Sign-in">
                {openPerson.account_email ? (
                  <span className="font-mono break-all">{openPerson.account_email}</span>
                ) : (
                  <span className="flex flex-col items-start gap-0.5">
                    <span className="text-ink-soft">No login</span>
                    {openPerson.roster_email &&
                      pendingEmails.has(openPerson.roster_email.trim().toLowerCase()) && (
                        <span className="text-xs text-caution-deep">
                          Login ready, links on first sign-in
                        </span>
                      )}
                  </span>
                )}
              </DrawerRow>
              {openPerson.user_id && (
                <DrawerActionRow label="Access">
                  <span className="text-ink">
                    {accessRoleLabel(openPerson.access_role)}
                    {openPerson.account_is_active === false && (
                      <span className="text-ink-soft"> (deactivated)</span>
                    )}
                  </span>
                  <TextAction
                    disabled={accessBusy(openPerson.user_id)}
                    onClick={() =>
                      openPerson.user_id &&
                      openPerson.access_role &&
                      setSheet({
                        kind: 'role',
                        userId: openPerson.user_id,
                        name: personName(openPerson),
                        current: openPerson.access_role,
                      })
                    }
                  >
                    Change…
                  </TextAction>
                  {openPerson.account_is_active === false ? (
                    <TextAction
                      disabled={rowBusy(openPerson)}
                      onClick={() =>
                        openPerson.user_id &&
                        setSheet({
                          kind: 'active',
                          userId: openPerson.user_id,
                          name: personName(openPerson),
                          makeActive: true,
                        })
                      }
                    >
                      Reactivate
                    </TextAction>
                  ) : (
                    <DestructiveTextAction
                      disabled={rowBusy(openPerson)}
                      onClick={() =>
                        openPerson.user_id &&
                        setSheet({
                          kind: 'active',
                          userId: openPerson.user_id,
                          name: personName(openPerson),
                          makeActive: false,
                        })
                      }
                    >
                      Revoke
                    </DestructiveTextAction>
                  )}
                </DrawerActionRow>
              )}
              <DrawerActionRow label="Account">
                {openPerson.kind === 'linked' ? (
                  <DestructiveTextAction
                    disabled={rowBusy(openPerson)}
                    onClick={() =>
                      openPerson.roster_id && unlinkMutation.mutate(openPerson.roster_id)
                    }
                  >
                    Unlink
                  </DestructiveTextAction>
                ) : (
                  <>
                    {openPerson.suggested_user_id && (
                      <TextAction
                        disabled={rowBusy(openPerson)}
                        onClick={() =>
                          openPerson.roster_id &&
                          openPerson.suggested_user_id &&
                          linkMutation.mutate({
                            rosterId: openPerson.roster_id,
                            userId: openPerson.suggested_user_id,
                            name: personName(openPerson),
                          })
                        }
                      >
                        Link to {openPerson.suggested_user_display_name ?? openPerson.suggested_user_email}
                      </TextAction>
                    )}
                    {!openPerson.suggested_user_id && openPerson.suggested_ambiguous && (
                      <span className="text-ink-soft">Multiple account matches</span>
                    )}
                    <TextAction
                      disabled={rowBusy(openPerson)}
                      onClick={() => setSheet({ kind: 'linkToAccount', row: openPerson })}
                    >
                      Link to an account…
                    </TextAction>
                  </>
                )}
              </DrawerActionRow>
            </DrawerRowList>
          </DrawerSection>

          <DrawerSection title="Profile" testId="person-drawer-profile">
            <PersonProfileGrid
              tenantSlug={tenantSlug}
              rosterId={openPerson.roster_id}
              name={openPerson.full_name}
              identity={{
                signsAs: openPerson.preferred_name ?? '',
                signsAsPlaceholder: (openPerson.full_name ?? '').split(/\s+/)[0] ?? '',
                credentials: openPerson.credentials ?? '',
                onSaveSignsAs: (name) =>
                  openPerson.roster_id &&
                  preferredNameMutation.mutate({ rosterId: openPerson.roster_id, name }),
                onSaveCredentials: (credentials) =>
                  openPerson.roster_id &&
                  credentialsMutation.mutate({ rosterId: openPerson.roster_id, credentials }),
              }}
              bio={openPerson.bio}
              signatureBlock={openPerson.signature_block ?? null}
              // 0161 intro-email inputs: contact facts + name derivation. The
              // email is roster-first, sign-in address second (contactEmail) —
              // the placeholder is reserved for someone with neither.
              email={contactEmail(openPerson.roster_email, openPerson.account_email)}
              phone={openPerson.phone}
              unitId={openPerson.unit_id}
              unitOptions={unitOptions}
              targetWeeklyHours={openPerson.target_weekly_hours}
              credentials={openPerson.credentials}
              preferredName={openPerson.preferred_name}
              doesCareManagement={openPerson.does_care_management}
              doesSpice={openPerson.does_spice}
              hasPhoto={openPerson.has_photo}
              version={photoVersions[openPerson.roster_id]}
              busy={rowBusy(openPerson)}
              // Narrower than `busy`: the unit select disables on its OWN
              // pending save only, same reasoning as designationBusy/accessBusy
              // above — an unrelated hung save on this row must not grey it
              // out too. `busy` stays the photo/switches' disabled reading.
              unitBusy={
                unitMutation.isPending && unitMutation.variables?.rosterId === openPerson.roster_id
              }
              onSaveBio={(bio) =>
                openPerson.roster_id && bioMutation.mutate({ rosterId: openPerson.roster_id, bio })
              }
              onSavePhone={(phone) =>
                openPerson.roster_id &&
                phoneMutation.mutate({ rosterId: openPerson.roster_id, phone })
              }
              onSaveUnit={(unitId) =>
                openPerson.roster_id &&
                unitMutation.mutate({ rosterId: openPerson.roster_id, unitId })
              }
              onSaveTargetWeeklyHours={(targetWeeklyHours) =>
                openPerson.roster_id &&
                targetHoursMutation.mutate({ rosterId: openPerson.roster_id, targetWeeklyHours })
              }
              onToggleDoesCareManagement={(doesCareManagement) =>
                openPerson.roster_id &&
                doesCareManagementMutation.mutate({
                  rosterId: openPerson.roster_id,
                  doesCareManagement,
                })
              }
              onToggleDoesSpice={(doesSpice) =>
                openPerson.roster_id &&
                doesSpiceMutation.mutate({ rosterId: openPerson.roster_id, doesSpice })
              }
              onUploadPhoto={async (data, mime) => {
                if (!openPerson.roster_id) return;
                await photoUploadMutation.mutateAsync({ rosterId: openPerson.roster_id, data, mime });
              }}
              onRemovePhoto={() =>
                openPerson.roster_id && photoDeleteMutation.mutate(openPerson.roster_id)
              }
            />
          </DrawerSection>

          <DrawerSection title="Directory" testId="person-drawer-directory">
            <DirectoryFieldsEditor
              rosterId={openPerson.roster_id}
              name={openPerson.full_name}
              fields={openPerson}
              privateContactVisible={staffQuery.data?.private_contact_visible === true}
              busy={rowBusy(openPerson)}
              onSave={(patch) =>
                openPerson.roster_id &&
                directoryMutation.mutate({ rosterId: openPerson.roster_id, patch })
              }
              onSaveHome={async (parts) => {
                const rosterId = openPerson.roster_id;
                if (!rosterId) return 'not_attempted';
                return homeSaveOutcome(await directoryMutation.mutateAsync({ rosterId, patch: { home_address: parts } }));
              }}
            />
          </DrawerSection>
        </PersonDrawer>
      )}
    </div>
  );
}

/** "27 people" / "1 person", with a quiet care-manager count after a
 *  middot when that's a useful narrower fact ("27 people · 16 care
 *  managers"): the People header's live count, computed from the rows on
 *  screen. Counts everyone the list shows, not one role among them. */
function peopleCountLabel(total: number, careManagers: number): string {
  const people = `${total} ${total === 1 ? 'person' : 'people'}`;
  if (careManagers === 0 || careManagers === total) return people;
  return `${people} · ${careManagers} care manager${careManagers === 1 ? '' : 's'}`;
}

const MONTH_OPTIONS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** The birthday pair as typed: nothing, half of it, or a month with a day. */
export type BirthdayPairInput =
  | { kind: 'empty' }
  | { kind: 'incomplete' }
  | { kind: 'set'; month: number; day: number };

/**
 * Read the two birthday controls together. The pair is one fact (the server
 * refuses a month without its day, and the reverse), so a form treats
 * 'incomplete' as not-yet-savable rather than saving half.
 */
export function birthdayPairFromText(monthText: string, dayText: string): BirthdayPairInput {
  if (monthText === '' && dayText === '') return { kind: 'empty' };
  const month = Number(monthText);
  const day = Number(dayText);
  if (
    monthText === '' ||
    dayText === '' ||
    !Number.isInteger(month) || month < 1 || month > 12 ||
    !Number.isInteger(day) || day < 1 || day > 31
  ) {
    return { kind: 'incomplete' };
  }
  return { kind: 'set', month, day };
}

/** The refusal a form shows for a half-entered birthday, before any request. */
export const BIRTHDAY_PAIR_MESSAGE = 'Enter both the birthday month and day, or neither.';

/** The directory fields (0176) as an editor reads them off either row shape,
 *  plus the map home point's town and ZIP (read-only: the address save
 *  keeps them). */
export interface DirectoryFieldValues {
  birthday_month?: number | null;
  birthday_day?: number | null;
  work_anniversary?: string | null;
  start_date?: string | null;
  home_address?: string | null;
  /** The home's five parts (0228); null on every part: text only. */
  home_address_line1?: string | null;
  home_address_line2?: string | null;
  home_address_city?: string | null;
  home_address_state?: string | null;
  home_address_zip?: string | null;
  home_city?: string | null;
  home_zip?: string | null;
  personal_phone?: string | null;
}

/** "Montclair 07042", or whichever half is on file; null when neither is. */
export function homePointLabel(city: string | null | undefined, zip: string | null | undefined): string | null {
  const parts = [city, zip].filter((v): v is string => typeof v === 'string' && v.trim() !== '');
  return parts.length === 0 ? null : parts.join(' ');
}

/**
 * The home-address line a read-only surface shows. The capacity map places
 * a person by staff_roster's home point, which the staff import seeded
 * with a town and ZIP before the directory's street-address field existed;
 * so "no address" and "not on the map" are different facts, and this says
 * which one is true.
 */
export function homeAddressLine(
  address: string | null | undefined,
  city: string | null | undefined,
  zip: string | null | undefined,
): string {
  if (typeof address === 'string' && address.trim() !== '') return address;
  const point = homePointLabel(city, zip);
  return point ? `Street not on file; the map places them in ${point}.` : 'Not on file';
}

/** The editor's note under the Home address field: what the map does with it. */
export function homeAddressHint(
  who: string,
  address: string | null | undefined,
  city: string | null | undefined,
  zip: string | null | undefined,
): string {
  if (typeof address === 'string' && address.trim() !== '') return 'The map places this person from this address.';
  const point = homePointLabel(city, zip);
  return point
    ? `Street not on file. The map places ${who} in ${point}; enter the address and the map will follow it.`
    : `Enter a home address to place ${who} on the map.`;
}

/**
 * What to tell the saver about the map after a directory save: null when
 * there is nothing to add (no address in the patch, or the address was
 * found and the map now uses it), otherwise why the map did NOT move.
 */
export function homePointSavedMessage(outcome: HomePointOutcome | undefined): string | null {
  switch (outcome) {
    case 'updated':
      return 'Directory details saved. The map now places this person from this address.';
    case 'no_match':
      return "Saved, but the map couldn't find this address. Check the street, town and ZIP, then save again.";
    case 'unavailable':
      return "Saved, but the map couldn't update just now. Save again in a few minutes to place them.";
    case 'cleared':
      return 'Directory details saved. The home address is removed, so the map no longer places this person.';
    default:
      return null;
  }
}

/** What a home save did to the map, in the address dialog's words. */
export function homeSaveOutcome(answer: { home_point?: string; home_precision?: string }): PostalSaveOutcome {
  if (answer.home_point === 'updated') return answer.home_precision === 'zip' ? 'approximate' : 'placed';
  if (answer.home_point === 'no_match') return 'no_match';
  if (answer.home_point === 'unavailable') return 'unavailable';
  return 'not_attempted';
}

/** A row's stored home parts, or null when the home is text only. */
function storedHomeParts(fields: DirectoryFieldValues): AddressFormParts | null {
  if (fields.home_address_line1 === undefined || fields.home_address_line1 === null) return null;
  return {
    line1: fields.home_address_line1,
    line2: fields.home_address_line2 ?? null,
    city: fields.home_address_city ?? null,
    state: fields.home_address_state ?? null,
    zip: fields.home_address_zip ?? null,
  };
}

/**
 * The sentence added to an "Added ..." toast when the person's home address
 * was saved but the map could not place them from it; null when there is
 * nothing to add (no address, or the map now uses it).
 */
export function homePointAddedNote(outcome: HomePointOutcome | undefined): string | null {
  switch (outcome) {
    case 'no_match':
      return "They were added, but the map couldn't find their home address. Check the street, town and ZIP on their record.";
    case 'unavailable':
      return "They were added, but the map couldn't update just now. Open their record later and save it again to place them.";
    default:
      return null;
  }
}

// =====================================================================
// DirectoryFieldsEditor — the Team directory's facts. Editable here on the
// People page for every manage_staff_profiles seat, and reused verbatim in
// the Directory roster's own row drawer for that same seat (a seat without
// the capability sees the same facts read-only there). Birthday is a month
// select + a day field with no year; it saves once both are set (or both
// cleared). The two dates save on change; the two free-text fields save on
// blur (the AutoSaveField model). The private pair renders only when the
// server served it (privateContactVisible): a seat that cannot read the
// address book gets no field for it, and the server refuses the write too.
// =====================================================================
export function DirectoryFieldsEditor({
  rosterId,
  name,
  fields,
  privateContactVisible,
  busy,
  onSave,
  onSaveHome,
}: {
  rosterId: string;
  name: string | null;
  fields: DirectoryFieldValues;
  privateContactVisible: boolean;
  busy: boolean;
  onSave: (patch: StaffDirectoryFieldsPatch) => void;
  /** Saves the home as its parts; answers what the save did to the map. */
  onSaveHome: (parts: AddressFormParts) => Promise<PostalSaveOutcome>;
}): ReactNode {
  const [editingHome, setEditingHome] = useState(false);
  const month = fields.birthday_month ?? null;
  const day = fields.birthday_day ?? null;
  const [monthText, setMonthText] = useState(month === null ? '' : String(month));
  const [dayText, setDayText] = useState(day === null ? '' : String(day));
  useEffect(() => setMonthText(month === null ? '' : String(month)), [month]);
  useEffect(() => setDayText(day === null ? '' : String(day)), [day]);
  const who = name ?? 'this person';

  // The pair saves together: both set (a real month + a day 1..31) or both
  // cleared. A half-entered pair waits; the server refuses it anyway.
  const commitBirthday = (m: string, d: string): void => {
    const pair = birthdayPairFromText(m, d);
    if (pair.kind === 'empty') {
      if (month !== null || day !== null) onSave({ birthday_month: null, birthday_day: null });
      return;
    }
    if (pair.kind === 'incomplete') return;
    if (pair.month === month && pair.day === day) return;
    onSave({ birthday_month: pair.month, birthday_day: pair.day });
  };

  const field =
    'min-h-9 px-2.5 py-1.5 text-sm border border-sage-800/[12%] rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400';

  return (
    <div className="flex flex-col gap-4" data-testid={`directory-fields-${rosterId}`}>
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-ink-soft">Birthday</span>
          <div className="flex gap-2">
            <SelectField size="compact"
              value={monthText}
              disabled={busy}
              aria-label={`Birthday month for ${who}`}
              data-testid={`directory-birthday-month-${rosterId}`}
              onChange={(e) => {
                setMonthText(e.target.value);
                commitBirthday(e.target.value, dayText);
              }}
              className="flex-1"
            >
              <option value="">Month</option>
              {MONTH_OPTIONS.map((label, i) => (
                <option key={label} value={String(i + 1)}>{label}</option>
              ))}
            </SelectField>
            <input
              type="number"
              min={1}
              max={31}
              value={dayText}
              disabled={busy}
              placeholder="Day"
              aria-label={`Birthday day for ${who}`}
              data-testid={`directory-birthday-day-${rosterId}`}
              onChange={(e) => setDayText(e.target.value)}
              onBlur={() => commitBirthday(monthText, dayText)}
              className={`${field} w-20`}
            />
          </div>
          <span className="text-xs text-ink-soft">Month and day only; no year is kept.</span>
        </div>

        <DateField
          id={`directory-start-date-${rosterId}`}
          label="Start date"
          value={fields.start_date ?? null}
          ariaLabel={`Start date for ${who}`}
          disabled={busy}
          onSave={(v) => onSave({ start_date: v })}
        />

        <DateField
          id={`directory-anniversary-${rosterId}`}
          label="Work anniversary"
          help="Leave blank to count from the start date."
          value={fields.work_anniversary ?? null}
          ariaLabel={`Work anniversary for ${who}`}
          disabled={busy}
          onSave={(v) => onSave({ work_anniversary: v })}
        />

        {privateContactVisible && (
          <AutoSaveField
            id={`directory-personal-phone-${rosterId}`}
            label="Personal phone"
            value={fields.personal_phone ?? ''}
            placeholder={PHONE_EXAMPLE}
            ariaLabel={`Personal phone for ${who}`}
            testid={`directory-personal-phone-${rosterId}`}
            onSave={(v) => onSave({ personal_phone: v.trim() === '' ? null : v })}
          />
        )}
      </div>

      {privateContactVisible && (
        // The home is the shared address form's (a street, town, state and
        // ZIP), edited in the postal address dialog; the row shows it as
        // text with what the map does with it beneath.
        <div className="flex flex-col gap-1" data-testid="staff-home-address-row">
          <div className="flex flex-wrap items-center justify-between gap-x-3">
            <span className={FLD_LABEL}>Home address</span>
            <span className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                data-testid="staff-home-address-edit"
                aria-label={`Edit home address for ${who}`}
                disabled={busy}
                onClick={() => setEditingHome(true)}
              >
                Edit
              </Button>
              {fields.home_address && (
                // Removing the home clears its map point too, as the
                // emptied box used to.
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="staff-home-address-remove"
                  aria-label={`Remove home address for ${who}`}
                  disabled={busy}
                  onClick={() => onSave({ home_address: null })}
                >
                  Remove
                </Button>
              )}
            </span>
          </div>
          <p className="text-sm text-sage-800 [overflow-wrap:anywhere]" data-testid={`directory-home-address-${rosterId}`}>
            {fields.home_address ?? 'Not on file'}
          </p>
          <p className="text-xs text-ink-soft" data-testid={`directory-home-address-hint-${rosterId}`}>
            {homeAddressHint(who, fields.home_address, fields.home_city, fields.home_zip)}
          </p>
        </div>
      )}
      {editingHome && (
        <PostalAddressDialog
          existing={storedHomeParts(fields)}
          existingText={fields.home_address ?? null}
          rule="home"
          onSave={(parts) => onSaveHome(parts)}
          onClose={() => setEditingHome(false)}
        />
      )}
      {privateContactVisible && (
        <p className="text-xs text-ink-soft">
          Home address and personal phone are shown only to the office seats; everyone else sees
          the work contacts, birthday, and anniversary.
        </p>
      )}
    </div>
  );
}

/** A date field that saves on change; clearing saves null. */
function DateField({
  id,
  label,
  help,
  value,
  ariaLabel,
  disabled,
  onSave,
}: {
  id: string;
  label: string;
  help?: string;
  value: string | null;
  ariaLabel: string;
  disabled: boolean;
  onSave: (v: string | null) => void;
}): ReactNode {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-ink-soft" htmlFor={id}>
        {label}
      </label>
      <DateInput
        id={id}
        value={value ?? ''}
        disabled={disabled}
        aria-label={ariaLabel}
        data-testid={id}
        onChange={(e) => {
          const next = e.target.value === '' ? null : e.target.value;
          if (next !== value) onSave(next);
        }}
        size="compact"
      />
      {help && <span className="text-xs text-ink-soft">{help}</span>}
    </div>
  );
}


// =====================================================================
// PersonDrawer — the person editor's chrome. The Drawer primitive plus the
// header every path shares (photo, name, a badge slot) and the pinned footer.
// The body is the composing path's own DrawerSections: the owner gets access +
// profile, the manage_staff_profiles editor gets profile only. Nothing
// capability-shaped lives here — each path passes only what it may show.
// =====================================================================

export function PersonDrawer({
  name,
  photo,
  badges,
  onClose,
  children,
}: {
  name: string;
  /** The avatar in the header, at the "lg" size (64px) every caller's photo
   *  component renders it at -- CmPhoto for the People-tab editor,
   *  TeamPhoto for the Directory roster. The chrome only lays it out; it
   *  never decides which door a photo is served through, because that
   *  door is capability-floored per caller (CmPhoto sits at view_workspace,
   *  TeamPhoto at the lower view_staff_tools hub floor a line CM also
   *  holds). */
  photo: ReactNode;
  /** Chips/controls beside the name (designation, unit, lifecycle state). */
  badges?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <Drawer
      onClose={onClose}
      ariaLabel={`${name} profile`}
      testId="person-drawer"
      widthStorageKey={PEOPLE_DRAWER_WIDTH_KEY}
      defaultWidth={560}
      footer={
        <div className="flex items-center justify-between gap-3 border-t border-sage-800/[8%] bg-paper px-4 py-3">
          {/* The DP4 §A model, said out loud: no Save button anywhere in the
              drawer, so the exit must not read as "discard". */}
          <span className="text-xs text-ink-soft">Changes save as you go.</span>
          <Button variant="secondary" size="sm" onClick={onClose} data-testid="person-drawer-done">
            Done
          </Button>
        </div>
      }
    >
      <header className="shrink-0 flex items-start gap-3 border-b border-sage-800/[8%] bg-paper px-4 py-3">
        {photo}
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-ink break-words">{name}</h2>
          {badges && <div className="mt-1 flex flex-wrap items-center gap-2">{badges}</div>}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={`Close ${name} profile`}
          className="min-h-9 min-w-9 -mr-1 inline-flex items-center justify-center text-ink-soft hover:text-ink rounded-md hover:bg-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          <X size={18} aria-hidden="true" />
        </button>
      </header>
      <div className="flex-1 min-h-0 overflow-auto flex flex-col gap-3 px-3 py-3">{children}</div>
    </Drawer>
  );
}

function personName(row: PersonRow): string {
  return row.full_name ?? row.account_display_name ?? row.account_email ?? 'this person';
}

// =====================================================================
// PersonRowView — one row per person (the merged view)
// =====================================================================

interface PersonRowViewProps {
  row: PersonRow;
  busy: boolean;
  tenantSlug: string;
  photoVersion?: number;
  /** Roster rows only — opens the person's editor drawer. */
  onOpen: () => void;
  onOpenRosterPicker: () => void;
  onChangeRole: () => void;
  /** Roster rows only — opens the same "Change role" sheet the drawer's
   *  header control does. */
  onChangeDesignation: () => void;
  onSetActive: (makeActive: boolean) => void;
  onArchive: () => void;
  onRestore: () => void;
  /** account_only stubs only — opens the destructive Remove confirm. */
  onRemoveLogin: () => void;
}

/**
 * One scannable row. A roster row is a single click target that opens the
 * editor drawer; the lifecycle menu sits OUTSIDE that target so the two never
 * nest. An account_only login stub has no roster record and therefore no
 * profile to open, so it keeps the #625 inline shape: the needs-attention chip
 * plus its two promised actions, "Link to a person" and the destructive Remove.
 */
function PersonRowView({
  row,
  busy,
  tenantSlug,
  photoVersion,
  onOpen,
  onOpenRosterPicker,
  onChangeRole,
  onChangeDesignation,
  onSetActive,
  onArchive,
  onRestore,
  onRemoveLogin,
}: PersonRowViewProps): ReactNode {
  const hasAccount = row.user_id !== null;
  const isRoster = row.roster_id !== null;
  const needsAttention = row.kind === 'account_only';
  const archived = row.roster_is_active === false;
  const inactive = archived || row.account_is_active === false;

  const displayName = personName(row);

  // The account line, shared by both row shapes.
  const accountLine = hasAccount ? (
    <>
      {row.account_email && (
        <span className="text-ink-soft font-mono truncate">{row.account_email}</span>
      )}
      <span className="inline-flex items-center px-2 py-0.5 font-medium bg-sage-50 text-sage-700 border border-sage-200 rounded-full">
        {accessRoleLabel(row.access_role)}
      </span>
      {inactive && <span className="text-ink-soft">Deactivated</span>}
      {needsAttention && (
        <span className="text-warm-700">No staff record. Link to a person or remove.</span>
      )}
    </>
  ) : (
    <span className={ROW_CHIP}>No login</span>
  );

  return (
    <div className={'flex items-center gap-2 px-2 py-1.5 ' + (needsAttention ? 'bg-warm-50' : '')}>
      {isRoster && row.roster_id ? (
        // The whole row is the click target (the ClientRatesTab precedent: a
        // two-line block affordance the Button primitive cannot express).
        <button
          type="button"
          onClick={onOpen}
          data-testid={`person-open-${row.roster_id}`}
          aria-label={`Open ${displayName}`}
          className="flex flex-1 min-w-0 items-center gap-3 text-left px-1 py-1 rounded-md hover:bg-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          <CmPhoto
            tenantSlug={tenantSlug}
            rosterId={row.roster_id}
            name={row.full_name}
            size="md"
            hasPhoto={row.has_photo}
            version={photoVersion}
          />
          <span className="flex-1 min-w-0 flex flex-col gap-0.5">
            <span className="flex flex-wrap items-center gap-2">
              <span className={'text-sm ' + (inactive ? 'text-ink-soft line-through' : 'text-ink')}>
                {displayName}
              </span>
              <span className={ROW_CHIP}>{billableRoleLabel(row.billable_role)}</span>
              {archived && <span className={ROW_CHIP}>Former staff</span>}
            </span>
            <span className="flex flex-wrap items-center gap-2 text-xs min-w-0">{accountLine}</span>
          </span>
          <ChevronRight size={16} className="shrink-0 text-sage-400" aria-hidden="true" />
        </button>
      ) : (
        <div className="flex flex-1 min-w-0 items-start gap-3 px-1 py-1">
          <div
            className={
              'w-9 h-9 rounded-full flex items-center justify-center text-cream font-semibold shrink-0 ' +
              (needsAttention ? 'bg-warm-400' : inactive ? 'bg-sage-300' : 'bg-sage-700')
            }
          >
            {(displayName[0] ?? '?').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className={'text-sm ' + (inactive ? 'text-ink-soft line-through' : 'text-ink')}>
                {displayName}
              </span>
              <span className={ROW_CHIP}>Login only</span>
              <span className="inline-flex items-center min-h-5 px-2 py-0.5 text-[11px] font-semibold bg-warm-100 text-warm-700 border border-warm-300 rounded-full">
                Needs attention
              </span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">{accountLine}</div>
            {/* The two actions the row copy promises ("Link to a person or
                remove"): link, or the destructive Remove (#625/#616). */}
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={onOpenRosterPicker}
                className="min-h-7 px-2 py-0.5 text-xs text-sage-700 border border-sage-300 rounded-full hover:bg-sage-50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                Link to a person…
              </button>
              <DestructiveTextAction
                disabled={busy}
                onClick={onRemoveLogin}
                aria-label={`Remove ${displayName}'s login`}
              >
                Remove
              </DestructiveTextAction>
            </div>
          </div>
        </div>
      )}

      {/* Lifecycle actions stay ON the row, outside the drawer opener: change
          access, deactivate, remove from list, restore. */}
      {(hasAccount || isRoster) && (
        <div className="shrink-0">
          <PersonActionsMenu
            displayName={displayName}
            hasAccount={hasAccount}
            accountInactive={row.account_is_active === false}
            canChangeRole={row.access_role !== null}
            canChangeDesignation={isRoster}
            canArchive={isRoster && !archived}
            canRestore={isRoster && archived}
            onChangeRole={onChangeRole}
            onChangeDesignation={onChangeDesignation}
            onSetActive={onSetActive}
            onArchive={onArchive}
            onRestore={onRestore}
          />
        </div>
      )}
    </div>
  );
}

/** A single-line text field that autosaves on blur when its value changed
 *  (DP4 §A: replaces the per-field Save buttons — "saves when you click
 *  away"). Idempotent: the dirty-check skips a no-op save. */
/** "20.00" reads as "20" in the input; "17.50" as "17.5". */
function formatTargetHours(value: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : value;
}

function AutoSaveField({
  id,
  label,
  value,
  placeholder,
  ariaLabel,
  testid,
  onSave,
}: {
  id: string;
  label: string;
  value: string;
  placeholder?: string;
  ariaLabel: string;
  testid?: string;
  onSave: (v: string) => void;
}): ReactNode {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  const commit = (): void => {
    if (text.trim() === value.trim()) return;
    onSave(text);
  };
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-ink-soft" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        placeholder={placeholder}
        data-testid={testid}
        className="min-h-9 px-2.5 py-1.5 text-sm border border-sage-800/[12%] rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        aria-label={ariaLabel}
      />
    </div>
  );
}

/** The roster/team-grouping unit picker (DP4 §A field register, a select
 *  rather than AutoSaveField's text input): every active unit offered,
 *  "No unit" an explicit choice, labelled by kind so a mixed list (CM
 *  geographic units + the SPICE service line) reads unambiguously. Saves
 *  immediately on change — the CapabilitySwitch model, since a select is a
 *  discrete choice rather than free text needing a blur-commit. */
function UnitSelectField({
  id,
  label,
  value,
  options,
  disabled,
  ariaLabel,
  testid,
  onSave,
}: {
  id: string;
  label: string;
  /** The selected unit id, or '' for "No unit". */
  value: string;
  options: UnitOption[];
  disabled?: boolean;
  ariaLabel: string;
  testid?: string;
  onSave: (v: string) => void;
}): ReactNode {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-ink-soft" htmlFor={id}>
        {label}
      </label>
      <SelectField size="compact"
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onSave(e.target.value)}
        data-testid={testid}
        aria-label={ariaLabel}
      >
        <option value="">No unit</option>
        {options.map((u) => (
          <option key={u.id} value={u.id}>
            {unitOptionLabel(u)}
          </option>
        ))}
      </SelectField>
    </div>
  );
}

/** A per-person capability switch (DP4 §A) — "Does care management" / "Does
 *  SPICE". A checkbox with the existing helper-text style; saves immediately
 *  on toggle (a switch, not a form). */
function CapabilitySwitch({
  testid,
  checked,
  disabled,
  onChange,
  label,
  help,
}: {
  testid: string;
  checked: boolean;
  disabled: boolean;
  onChange: (v: boolean) => void;
  label: string;
  /** Optional helper line (Soak #2 #24 dropped it for the rate switches). */
  help?: string;
}): ReactNode {
  return (
    <label className="flex items-center gap-2 text-sm text-ink cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="h-[16px] w-[16px] accent-[var(--color-sage-800)]"
      />
      <span className="flex flex-col">
        <span>{label}</span>
        {help && <span className="text-xs text-ink-soft">{help}</span>}
      </span>
    </label>
  );
}

// =====================================================================
// PersonProfileGrid — the aligned per-person editor (DP4 §A). One two-column
// grid: identity (signs-as + credentials; owner path only) on the left,
// presence (photo + the two capability switches "Does care management" /
// "Does SPICE") on the right, with the bio full-width below. Rendered inside
// the person drawer on BOTH paths (owner and the manage_staff_profiles
// editor). Text fields autosave on blur; the switches save immediately on
// toggle — one consistent
// "changes save themselves" model (replaces the three ragged Save buttons
// the founder flagged). A picked file goes through preparePhoto
// (src/lib/photoPrep.ts) first — it decodes whatever the browser can read,
// downscales + re-encodes it to fit the upload cap, and turns anything it
// can't handle into a plain-English inline message rather than a silent
// failure; the server re-validates what actually arrives
// (validators/imageUpload.ts).
// =====================================================================

/** Owner-path identity fields (signs-as + credentials). Omitted on the
 *  non-owner editor, which has no access to those owner-only fields. */
interface PersonProfileIdentity {
  signsAs: string;
  signsAsPlaceholder: string;
  credentials: string;
  onSaveSignsAs: (v: string) => void;
  onSaveCredentials: (v: string) => void;
}

function PersonProfileGrid({
  tenantSlug,
  rosterId,
  name,
  identity,
  bio,
  signatureBlock = null,
  email,
  phone,
  unitId,
  unitOptions,
  targetWeeklyHours,
  credentials,
  preferredName,
  doesCareManagement,
  doesSpice,
  hasPhoto,
  version,
  busy,
  unitBusy,
  onSaveBio,
  onSavePhone,
  onSaveUnit,
  onSaveTargetWeeklyHours,
  onToggleDoesCareManagement,
  onToggleDoesSpice,
  onUploadPhoto,
  onRemovePhoto,
}: {
  tenantSlug: string;
  rosterId: string;
  name: string | null;
  identity?: PersonProfileIdentity;
  bio: string | null;
  // 0239: the lines printed under the name; null = none.
  signatureBlock?: string | null;
  // 0161 intro-email inputs (both paths since the StaffProfileRow widening):
  // roster email + direct phone (contact lines), credentials (name clause),
  // preferred name (first-name derivation).
  email: string | null;
  phone: string | null;
  // Roster/team-grouping unit — null = none set. Every active unit is
  // offered regardless of kind (a service_line unit for a spice_therapist,
  // a cm_geographic unit for a care manager, or either for anyone else).
  unitId: string | null;
  unitOptions: UnitOption[];
  // Contracted weekly-hours target (numeric text); null = none set.
  targetWeeklyHours: string | null;
  credentials: string | null;
  preferredName: string | null;
  // 0143 — the per-staff care-management flag.
  doesCareManagement: boolean;
  // 0134 — the per-staff SPICE flag.
  doesSpice: boolean;
  hasPhoto?: boolean;
  version?: number;
  busy: boolean;
  // The unit select's OWN disabled reading — defaults to `busy` when unset
  // (the manage_staff_profiles-only caller), but the owner caller passes a
  // narrower flag so an unrelated pending save (a photo upload, a bio
  // autosave) never greys out a control it has nothing to do with.
  unitBusy?: boolean;
  onSaveBio: (bio: string) => void;
  // 0161 — the direct-phone autosave (mirrors the bio).
  onSavePhone: (phone: string) => void;
  // Saves immediately on change (the CapabilitySwitch model, not the
  // AutoSaveField blur model — a select is a discrete choice).
  onSaveUnit: (unitId: string | null) => void;
  // The target-hours autosave (mirrors the phone; empty clears).
  onSaveTargetWeeklyHours: (targetWeeklyHours: string) => void;
  onToggleDoesCareManagement: (v: boolean) => void;
  onToggleDoesSpice: (v: boolean) => void;
  onUploadPhoto: (data: string, mime: string) => Promise<void>;
  onRemovePhoto: () => void;
}): ReactNode {
  const contact = useSupportContact();
  const [bioText, setBioText] = useState(bio ?? '');
  useEffect(() => setBioText(bio ?? ''), [bio]);
  // preparing: resizing the picked file in the browser, before any network
  // call. error/saved carry the inline status line under the buttons — the
  // page-level toast (PeopleTab's onError) fires too, but the open drawer
  // covers it, which is the whole reason this exists.
  const [photoState, setPhotoState] = useState<
    { kind: 'idle' } | { kind: 'preparing' } | { kind: 'error'; message: string } | { kind: 'saved' }
  >({ kind: 'idle' });
  useEffect(() => setPhotoState({ kind: 'idle' }), [rosterId]);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const photoBusy = busy || photoState.kind === 'preparing';

  const onPickFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setPhotoState({ kind: 'preparing' });
    const prepared = await preparePhoto(file);
    if (!prepared.ok) {
      setPhotoState({ kind: 'error', message: prepared.message });
      return;
    }
    try {
      await onUploadPhoto(prepared.data, prepared.mime);
      setPhotoState({ kind: 'saved' });
    } catch (err) {
      setPhotoState({ kind: 'error', message: photoUploadErrorMessage(err, 'staff', contact) });
    }
  };

  const commitBio = (): void => {
    if (bioText.trim() === (bio ?? '').trim()) return;
    onSaveBio(bioText);
  };

  return (
    <div className="flex flex-col gap-4" data-testid={`cm-profile-${rosterId}`}>
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        {/* Identity (owner path only): signs-as + credentials */}
        {identity && (
          <div className="flex flex-col gap-3">
            <AutoSaveField
              id={`pref-${rosterId}`}
              label="Signs as"
              value={identity.signsAs}
              placeholder={identity.signsAsPlaceholder}
              ariaLabel={`Sign-off name for ${name ?? 'this person'}`}
              onSave={identity.onSaveSignsAs}
            />
            <AutoSaveField
              id={`cred-${rosterId}`}
              label="Credentials"
              value={identity.credentials}
              placeholder="BSW, CSW, CMC"
              ariaLabel={`Report credentials for ${name ?? 'this person'}`}
              onSave={identity.onSaveCredentials}
            />
          </div>
        )}

        {/* Presence: photo + the two capability switches */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <CmPhoto tenantSlug={tenantSlug} rosterId={rosterId} name={name} size="md" hasPhoto={hasPhoto} version={version} />
            <div className="flex items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*,.heic,.heif"
                className="hidden"
                data-testid={`cm-photo-input-${rosterId}`}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  void onPickFile(file);
                }}
              />
              <button
                type="button"
                disabled={photoBusy}
                onClick={() => fileRef.current?.click()}
                className="min-h-8 px-3 py-1 text-xs font-semibold text-sage-700 border border-sage-300 rounded-md hover:bg-sage-50 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                {hasPhoto === false ? 'Add photo' : 'Change photo'}
              </button>
              {hasPhoto !== false && (
                <button
                  type="button"
                  disabled={photoBusy}
                  onClick={onRemovePhoto}
                  data-testid={`cm-photo-remove-${rosterId}`}
                  className="min-h-8 px-3 py-1 text-xs text-ink-soft hover:text-pepper-deep border border-sage-800/[8%] rounded-md hover:bg-pepper-soft disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pepper/50"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-ink-soft">Any photo from your phone or computer.</p>
          {photoState.kind === 'preparing' && (
            <p className="text-xs text-ink-soft" data-testid={`cm-photo-status-${rosterId}`}>
              Preparing photo…
            </p>
          )}
          {photoState.kind === 'error' && (
            <p className="text-xs text-pepper" role="alert" data-testid={`cm-photo-error-${rosterId}`}>
              {photoState.message}
            </p>
          )}
          {photoState.kind === 'saved' && (
            <p className="text-xs text-sage-700" data-testid={`cm-photo-status-${rosterId}`}>
              Photo saved.
            </p>
          )}
          {/* The two capability switches (0143 + 0134). Each saves immediately
              on toggle: flips which rate-band families this person may pick
              when logging billable time. Soak #2 #24 (D10): short labels
              ("Care management" / "SPICE") under one "Can log these rates:"
              heading, no per-checkbox helper lines — and deliberately NOT
              "Care Manager" / "SPICE Therapist", which would clash with the
              read-only designation pill. */}
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-ink-soft">Can log these rates:</span>
            <CapabilitySwitch
              testid={`cm-does-care-management-${rosterId}`}
              checked={doesCareManagement}
              disabled={busy}
              onChange={onToggleDoesCareManagement}
              label="Care management"
            />
            <CapabilitySwitch
              testid={`cm-does-spice-${rosterId}`}
              checked={doesSpice}
              disabled={busy}
              onChange={onToggleDoesSpice}
              label="SPICE"
            />
          </div>
        </div>
      </div>

      {/* Bio (full width). Autosaves on blur; a few paragraphs is fine. */}
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-ink-soft" htmlFor={`bio-${rosterId}`}>
          Bio
        </label>
        <textarea
          id={`bio-${rosterId}`}
          value={bioText}
          onChange={(e) => setBioText(e.target.value)}
          onBlur={commitBio}
          rows={7}
          placeholder="An intro the office can send to families."
          data-testid={`cm-bio-input-${rosterId}`}
          className="px-2.5 py-2 text-sm border border-sage-800/[12%] rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
          aria-label={`Bio for ${name ?? 'this person'}`}
        />
      </div>

      {/* 0239: the lines printed under this person's name on the
          documents they sign. Saves on leaving the box, as the bio does. */}
      <SignatureBlockField
        tenantSlug={tenantSlug}
        rosterId={rosterId}
        name={name ?? null}
        value={signatureBlock}
        testId={`cm-signature-block-input-${rosterId}`}
      />

      {/* 0161 — direct phone (autosaves on blur, mirrors the bio) feeding the
          intro email's "Phone." contact line. */}
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <AutoSaveField
          id={`phone-${rosterId}`}
          label="Direct phone"
          value={phone ?? ''}
          placeholder="The number families can call"
          ariaLabel={`Direct phone for ${name ?? 'this person'}`}
          testid={`cm-phone-input-${rosterId}`}
          onSave={onSavePhone}
        />
        <AutoSaveField
          id={`target-hours-${rosterId}`}
          label="Target hours per week"
          value={targetWeeklyHours === null ? '' : formatTargetHours(targetWeeklyHours)}
          placeholder="Blank means no target"
          ariaLabel={`Target hours per week for ${name ?? 'this person'}`}
          testid={`cm-target-hours-input-${rosterId}`}
          onSave={onSaveTargetWeeklyHours}
        />
        {/* Roster/team-grouping unit — every active unit offered, labelled
            by kind so a mixed list (CM geographic units + the SPICE
            service line) reads unambiguously. Saves immediately on
            change (a select is a discrete choice, not autosave-on-blur
            text). */}
        <UnitSelectField
          id={`unit-${rosterId}`}
          label="Unit"
          value={unitId ?? ''}
          options={unitOptions}
          disabled={unitBusy ?? busy}
          ariaLabel={`Unit for ${name ?? 'this person'}`}
          testid={`cm-unit-select-${rosterId}`}
          onSave={(v) => onSaveUnit(v === '' ? null : v)}
        />
      </div>

      {/* 0161 — the one-click Outlook-ready intro email assembled from this
          profile; highlighted placeholders mark what the unit lead fills. */}
      <IntroEmailSection
        tenantSlug={tenantSlug}
        rosterId={rosterId}
        fullName={name ?? ''}
        credentials={credentials}
        preferredName={preferredName}
        bio={bio}
        email={email}
        phone={phone}
        doesCareManagement={doesCareManagement}
        doesSpice={doesSpice}
        hasPhoto={hasPhoto !== false}
        version={version}
      />
    </div>
  );
}

/**
 * Slice 3c — the non-owner editor surface (admin_support / data_steward /
 * exec_support). A minimal CM list (photo + name + unit) with ONLY the
 * bio/photo editor, opened in the same person drawer the owner path uses;
 * none of the owner-only People controls render here. Fed by the
 * manage_staff_profiles GET /staff (StaffProfileRow — no link/credentials).
 */
function StaffProfileList({
  tenantSlug,
  profiles,
  isLoading,
  error,
  onRetryProfiles,
  toast,
  photoVersions,
  profileBusy,
  onAddPerson,
  onSaveBio,
  onSavePhone,
  unitOptions,
  onSaveUnit,
  privateContactVisible,
  onSaveDirectory,
  onSaveDirectoryHome,
  onSaveTargetWeeklyHours,
  onToggleDoesCareManagement,
  onToggleDoesSpice,
  onUploadPhoto,
  onRemovePhoto,
}: {
  tenantSlug: string;
  profiles: StaffProfileRow[];
  isLoading: boolean;
  error: Error | null;
  /** Asks for the profile list again after a failed load. */
  onRetryProfiles: () => void;
  toast: string | null;
  photoVersions: Record<string, number>;
  profileBusy: (rosterId: string | null) => boolean;
  /** Opens the one-door "Add a person" sheet (login section read-only for
   *  this actor — manage_staff_profiles cannot grant a login). */
  onAddPerson: () => void;
  onSaveBio: (rosterId: string, bio: string) => void;
  // 0161 — the direct-phone autosave (mirrors the bio).
  onSavePhone: (rosterId: string, phone: string) => void;
  /** The units catalog for the unit editor — every active unit, offered
   *  regardless of kind. */
  unitOptions: UnitOption[];
  onSaveUnit: (rosterId: string, unitId: string | null) => void;
  /** Whether the server served the private pair (the caller holds
   *  view_staff_private_contact); the editor renders those fields iff so. */
  privateContactVisible: boolean;
  onSaveDirectory: (rosterId: string, patch: StaffDirectoryFieldsPatch) => void;
  /** Saves a home as its parts; answers what the save did to the map. */
  onSaveDirectoryHome: (rosterId: string, parts: AddressFormParts) => Promise<PostalSaveOutcome>;
  onSaveTargetWeeklyHours: (rosterId: string, targetWeeklyHours: string) => void;
  onToggleDoesCareManagement: (rosterId: string, doesCareManagement: boolean) => void;
  onToggleDoesSpice: (rosterId: string, doesSpice: boolean) => void;
  onUploadPhoto: (rosterId: string, data: string, mime: string) => Promise<void>;
  onRemovePhoto: (rosterId: string) => void;
}): ReactNode {
  const [openRosterId, setOpenRosterId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const showSearch = profiles.length > PEOPLE_SEARCH_MIN;
  const query = search.trim().toLowerCase();
  const matched =
    query.length === 0
      ? profiles
      : profiles.filter((p) =>
          [p.full_name, p.unit_name, p.email]
            .filter((v): v is string => typeof v === 'string' && v.length > 0)
            .join(' ')
            .toLowerCase()
            .includes(query),
        );
  // Re-derived from the full list so filtering never closes an open drawer.
  const open = openRosterId === null ? null : (profiles.find((p) => p.roster_id === openRosterId) ?? null);

  return (
    <div>
      {toast && (
        <div role="status" aria-live="polite" className="mb-3 px-3 py-2 text-sm bg-sage-50 border border-sage-200 text-sage-800 rounded-md">
          {toast}
        </div>
      )}
      {/* The roster holds care managers, SPICE therapists and office staff
          alike, so the list copy names no role; the person's own flags decide
          what the intro email calls them. */}
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-sm font-semibold text-ink">Profiles</h3>
          {/* The live count from the rows on screen (this list is
              active-only), with how many are care managers held quietly
              alongside. */}
          <span className="text-xs text-ink-soft" data-testid="people-cm-count">
            {peopleCountLabel(
              profiles.length,
              profiles.filter((p) => p.billable_role === 'care_manager').length,
            )}
          </span>
        </div>
        <button
          type="button"
          onClick={onAddPerson}
          data-testid="add-person-open"
          className="min-h-9 px-3 py-1.5 text-xs font-semibold text-sage-700 border border-sage-300 rounded-md hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          + Add a person
        </button>
      </div>
      {showSearch && (
        <div className="mb-3">
          <label className="sr-only" htmlFor="staff-profiles-search">
            Find a person
          </label>
          <SearchField
            id="staff-profiles-search"
            value={search}
            onChange={setSearch}
            placeholder="Find a person"
            testId="staff-profiles-search"
          />
        </div>
      )}
      {isLoading && <div className="text-sm text-ink-soft py-6 text-center">Loading…</div>}
      {error && (
        <ErrorBanner
          error={error}
          what="the people list"
          onRetry={onRetryProfiles}
          data-testid="staff-profiles-load-error"
        />
      )}
      {!isLoading && !error && (
        <div
          className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 divide-y divide-sage-800/[8%]"
          data-testid="staff-profiles-list"
        >
          {matched.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-ink-soft">
              {query.length > 0 ? 'No one matches that search.' : 'Add the first person.'}
            </div>
          )}
          {matched.map((p) => (
            <div key={p.roster_id} className="px-2 py-1.5" data-testid={`staff-profile-row-${p.roster_id}`}>
              <button
                type="button"
                onClick={() => setOpenRosterId(p.roster_id)}
                data-testid={`person-open-${p.roster_id}`}
                aria-label={`Open ${p.full_name}`}
                className="flex w-full min-w-0 items-center gap-3 text-left px-1 py-1 rounded-md hover:bg-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
              >
                <CmPhoto
                  tenantSlug={tenantSlug}
                  rosterId={p.roster_id}
                  name={p.full_name}
                  size="md"
                  hasPhoto={p.has_photo}
                  version={photoVersions[p.roster_id]}
                />
                <span className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
                  <span className="text-sm text-ink font-semibold">{p.full_name}</span>
                  {p.unit_name && <span className={ROW_CHIP}>{p.unit_name}</span>}
                </span>
                <ChevronRight size={16} className="shrink-0 text-sage-400" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <PersonDrawer
          name={open.full_name}
          photo={
            <CmPhoto
              tenantSlug={tenantSlug}
              rosterId={open.roster_id}
              name={open.full_name}
              size="lg"
              hasPhoto={open.has_photo}
              version={photoVersions[open.roster_id]}
            />
          }
          onClose={() => setOpenRosterId(null)}
          badges={open.unit_name ? <span className={ROW_CHIP}>{open.unit_name}</span> : null}
        >
          <DrawerSection title="Profile" testId="person-drawer-profile">
            <PersonProfileGrid
              tenantSlug={tenantSlug}
              rosterId={open.roster_id}
              name={open.full_name}
              bio={open.bio}
              signatureBlock={open.signature_block ?? null}
              // 0161 intro-email inputs (?? null tolerates an older cached
              // wire shape without the widened fields). `email` already
              // resolves roster-then-account server-side (StaffProfileRow).
              email={open.email ?? null}
              phone={open.phone ?? null}
              unitId={open.unit_id}
              unitOptions={unitOptions}
              targetWeeklyHours={open.target_weekly_hours ?? null}
              credentials={open.credentials ?? null}
              preferredName={open.preferred_name ?? null}
              doesCareManagement={open.does_care_management === true}
              doesSpice={open.does_spice === true}
              hasPhoto={open.has_photo}
              version={photoVersions[open.roster_id]}
              busy={profileBusy(open.roster_id)}
              onSaveBio={(bio) => onSaveBio(open.roster_id, bio)}
              onSavePhone={(phone) => onSavePhone(open.roster_id, phone)}
              onSaveUnit={(unitId) => onSaveUnit(open.roster_id, unitId)}
              onSaveTargetWeeklyHours={(v) => onSaveTargetWeeklyHours(open.roster_id, v)}
              onToggleDoesCareManagement={(doesCareManagement) =>
                onToggleDoesCareManagement(open.roster_id, doesCareManagement)
              }
              onToggleDoesSpice={(doesSpice) => onToggleDoesSpice(open.roster_id, doesSpice)}
              onUploadPhoto={(data, mime) => onUploadPhoto(open.roster_id, data, mime)}
              onRemovePhoto={() => onRemovePhoto(open.roster_id)}
            />
          </DrawerSection>

          <DrawerSection title="Directory" testId="person-drawer-directory">
            <DirectoryFieldsEditor
              rosterId={open.roster_id}
              name={open.full_name}
              fields={open}
              privateContactVisible={privateContactVisible}
              busy={profileBusy(open.roster_id)}
              onSave={(patch) => onSaveDirectory(open.roster_id, patch)}
              onSaveHome={(parts) => onSaveDirectoryHome(open.roster_id, parts)}
            />
          </DrawerSection>
        </PersonDrawer>
      )}
    </div>
  );
}

function PersonActionsMenu({
  displayName,
  hasAccount,
  accountInactive,
  canChangeRole,
  canChangeDesignation,
  canArchive,
  canRestore,
  onChangeRole,
  onChangeDesignation,
  onSetActive,
  onArchive,
  onRestore,
}: {
  displayName: string;
  hasAccount: boolean;
  accountInactive: boolean;
  canChangeRole: boolean;
  /** Roster rows only — the operating role (billable_role) has a record to
   *  change. */
  canChangeDesignation: boolean;
  canArchive: boolean;
  canRestore: boolean;
  onChangeRole: () => void;
  onChangeDesignation: () => void;
  onSetActive: (makeActive: boolean) => void;
  onArchive: () => void;
  onRestore: () => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`More actions for ${displayName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="min-h-9 min-w-9 px-2 py-1 inline-flex items-center justify-center text-ink-soft hover:text-ink rounded-md hover:bg-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
      >
        <MoreHorizontal size={18} aria-hidden="true" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-9 bg-paper border border-sage-800/[8%] rounded-md shadow-lg min-w-[180px] z-10 py-1">
          {canChangeDesignation && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onChangeDesignation();
              }}
              className="block w-full text-left px-3 py-2 text-sm text-ink hover:bg-sand"
            >
              Change role…
            </button>
          )}
          {canChangeRole && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onChangeRole();
              }}
              className="block w-full text-left px-3 py-2 text-sm text-ink hover:bg-sand"
            >
              Change access
            </button>
          )}
          {hasAccount && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSetActive(accountInactive);
              }}
              className="block w-full text-left px-3 py-2 text-sm text-ink hover:bg-sand"
            >
              {accountInactive ? 'Reactivate' : 'Remove access'}
            </button>
          )}
          {canArchive && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onArchive();
              }}
              className="block w-full text-left px-3 py-2 text-sm text-pepper-deep hover:bg-pepper-soft"
            >
              Remove from Olive
            </button>
          )}
          {canRestore && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onRestore();
              }}
              className="block w-full text-left px-3 py-2 text-sm text-ink hover:bg-sand"
            >
              Restore
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// PendingRow — queued invites (revoke)
// =====================================================================

function PendingRow({ pending, onRevoke }: { pending: PendingGrant; onRevoke: () => void }): ReactNode {
  return (
    <div className="flex items-start gap-3 px-3 py-3 bg-gold-soft/30">
      <div className="w-9 h-9 rounded-full flex items-center justify-center text-caution-deep font-semibold bg-gold-soft border border-warm-200 shrink-0">
        {(pending.display_name ?? pending.email)[0]?.toUpperCase() ?? '?'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink">{pending.display_name ?? pending.email}</div>
        <div className="text-xs text-ink-soft font-mono truncate">{pending.email}</div>
        <div className="flex items-center gap-2 mt-1">
          <span className="inline-flex items-center min-h-6 px-2 py-0.5 text-xs font-medium bg-sand text-ink-soft border border-sage-800/[8%] rounded-full">
            {accessRoleLabel(pending.role)}
          </span>
          <span className="text-xs text-ink-soft">·</span>
          <span className="text-xs text-caution-deep">Login ready, not signed in yet</span>
        </div>
      </div>
      <button
        type="button"
        onClick={onRevoke}
        aria-label={`Remove the login waiting for ${pending.email}`}
        className="min-h-9 px-2 py-1 text-xs text-ink-soft hover:text-pepper-deep rounded-md hover:bg-pepper-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pepper/50"
      >
        Remove
      </button>
    </div>
  );
}

// =====================================================================
// LinkPickerSheet — generic picker (account list OR roster list)
// =====================================================================

interface LinkOption {
  id: string;
  primary: string;
  secondary: string | null;
}

function LinkPickerSheet({
  title,
  options,
  emptyLabel = 'No matches.',
  busy,
  onClose,
  onPick,
}: {
  title: string;
  options: LinkOption[];
  emptyLabel?: string;
  busy: boolean;
  onClose: () => void;
  onPick: (id: string) => void;
}): ReactNode {
  const [filter, setFilter] = useState('');
  useEffect(() => setFilter(''), []);
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (q.length === 0) return options;
    return options.filter(
      (o) => o.primary.toLowerCase().includes(q) || (o.secondary ?? '').toLowerCase().includes(q),
    );
  }, [options, filter]);

  return (
    <BottomSheet isOpen={true} onClose={onClose} title={title}>
      <div className="p-3 space-y-3">
        <SearchField
          value={filter}
          onChange={setFilter}
          aria-label="Search by name or email"
          placeholder="Search by name"
          testId="link-picker-search"
        />
        <div className="border border-sage-800/[8%] rounded-md divide-y divide-sage-800/[8%] max-h-72 overflow-y-auto">
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-ink-soft">{emptyLabel}</div>
          )}
          {filtered.map((o) => (
            <button
              key={o.id}
              type="button"
              disabled={busy}
              onClick={() => onPick(o.id)}
              className="block w-full text-left px-3 py-2 hover:bg-sand disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              <div className="text-sm text-ink">{o.primary}</div>
              {o.secondary && <div className="text-xs text-ink-soft font-mono truncate">{o.secondary}</div>}
            </button>
          ))}
        </div>
      </div>
    </BottomSheet>
  );
}

// =====================================================================
// RoleChangeSheet — change a member's access role
// =====================================================================

function RoleChangeSheet({
  name,
  current,
  busy,
  onClose,
  onConfirm,
}: {
  name: string;
  current: UserTenantRole;
  busy: boolean;
  onClose: () => void;
  onConfirm: (role: UserTenantRole) => void;
}): ReactNode {
  const [selected, setSelected] = useState<UserTenantRole>(current);
  useEffect(() => setSelected(current), [current]);
  return (
    <BottomSheet isOpen={true} onClose={onClose} title={`Change ${name}'s access`}>
      <div className="p-3 space-y-3">
        <div role="radiogroup" aria-label="New access role" className="space-y-1">
          {TEAM_GRANTABLE_ROLES.map((r) => (
            <label
              key={r}
              className={
                'flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer ' +
                (selected === r ? 'bg-sage-50 border border-sage-300' : 'border border-transparent hover:bg-sand')
              }
            >
              <input
                type="radio"
                name="role"
                value={r}
                checked={selected === r}
                onChange={() => setSelected(r)}
                className="text-sage-600 focus-visible:ring-sage-400"
              />
              {/* One name per seat (founder ruling 2026-08-27). This line
                  used to render the access label and the team label side by
                  side — "Clinical Team · Primary CM" — which asked the
                  person assigning a role to reconcile two vocabularies at
                  the moment of choosing. They are now the same string, so
                  the second half would read as an echo. The blurb carries
                  what the middot half was reaching for. */}
              <span className="text-sm">
                {accessRoleLabel(r)}
                <span className="block text-xs text-ink-soft">{roleBlurb(r)}</span>
              </span>
              {current === r && <span className="text-xs text-ink-soft ml-auto">current</span>}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="min-h-11 px-4 py-2 text-sm text-ink-soft hover:text-sage-800">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || selected === current}
            onClick={() => onConfirm(selected)}
            className="min-h-11 px-4 py-2 text-sm bg-sage-700 text-cream rounded-md hover:bg-sage-800 disabled:opacity-50"
          >
            {busy ? 'Saving…' : `Change to ${accessRoleLabel(selected)}`}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

// =====================================================================
// DesignationChangeSheet — change a person's role (staff_roster.billable_role,
// what the rest of the app calls the "designation"). Parallel to
// RoleChangeSheet (which changes the ACCESS role). The server derives the
// implied rate flags on a real change, so picking Care Manager / Manager /
// SPICE Therapist checks the matching "Can log these rates:" switch after
// the refetch — the sheet says so itself, under the options, rather than
// leaving that for the operator to notice later.
//
// A refusal renders INLINE (the `error` prop, read off the mutation, never a
// page toast): the sheet — and the drawer under it — cover the toast, so a
// failure said only there would be invisible until it auto-dismissed.
// =====================================================================

/** The billable-role sheet's plain-words reading of a save failure. 403/404
 *  get fixed copy; anything else reads as a People refusal. */
function designationErrorMessage(err: unknown, contact: SupportContact): string {
  if (err instanceof ApiError) {
    if (err.status === 403) return contactSentence("You can't change someone's access. Ask {contact_admin} to make this change.", contact);
    if (err.status === 404) return 'That person is no longer on the roster. Reload the page to see the current list.';
  }
  return peopleRefusalText(err, contact);
}

function DesignationChangeSheet({
  name,
  current,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  name: string;
  current: string;
  busy: boolean;
  /** The last save attempt's failure, if any (react-query's mutation.error). */
  error: unknown;
  onClose: () => void;
  onConfirm: (role: BillableRole) => void;
}): ReactNode {
  const initial: BillableRole = (BILLABLE_ROLES as readonly string[]).includes(current)
    ? (current as BillableRole)
    : BILLABLE_ROLES[0];
  const contact = useSupportContact();
  const [selected, setSelected] = useState<BillableRole>(initial);
  useEffect(() => setSelected(initial), [initial]);
  // The rate flag THIS choice would turn on, same gate the server uses
  // (flagsImpliedByBillableRole only fires on a real change) — said here so
  // the operator learns it before saving, not after, from the switches.
  const impliedPhrase = selected !== current ? impliedRatePhrase(selected) : null;
  return (
    <BottomSheet isOpen={true} onClose={onClose} title={`Change role for ${name}`}>
      <div className="p-3 space-y-3">
        <div role="radiogroup" aria-label="New role" className="space-y-1">
          {BILLABLE_ROLES.map((r) => (
            <label
              key={r}
              className={
                'flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer ' +
                (selected === r
                  ? 'bg-sage-50 border border-sage-300'
                  : 'border border-transparent hover:bg-sand')
              }
            >
              <input
                type="radio"
                name="designation"
                value={r}
                checked={selected === r}
                onChange={() => setSelected(r)}
                className="text-sage-600 focus-visible:ring-sage-400"
              />
              <span className="text-sm">
                {billableRoleLabel(r)}
                <span className="block text-xs text-ink-soft">{billableRoleBlurb(r)}</span>
              </span>
              {current === r && <span className="text-xs text-ink-soft ml-auto">current</span>}
            </label>
          ))}
        </div>
        {impliedPhrase && (
          <p className="text-xs text-ink-soft">
            {name} will also be able to {impliedPhrase}.
          </p>
        )}
        <p className="text-xs text-ink-soft">
          Their unit and any assigned clients stay as they are. The unit is edited separately in the
          drawer.
        </p>
        {error !== null && error !== undefined && (
          <p className="text-xs text-pepper-deep" role="alert" data-testid="designation-error">
            {designationErrorMessage(error, contact)}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="min-h-11 px-4 py-2 text-sm text-ink-soft hover:text-sage-800">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy || selected === current}
            onClick={() => onConfirm(selected)}
            className="min-h-11 px-4 py-2 text-sm bg-sage-700 text-cream rounded-md hover:bg-sage-800 disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Change role'}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

// =====================================================================
// AddPersonSheet — the one door for adding a person: "we should just have one
// button - add to Olive, and then the role, and then all the other
// pieces". Writes the roster row and, by default, its login together
// (POST /admin-review/people, repos/people.ts createPersonWithAccess, one
// transaction). The genuine gap this closes: every roster row used to
// arrive through migration seed or operator SQL, and a login was a whole
// separate errand through a second button.
//
// canGrantLogin decides which server route the save takes, not just which
// fields render: true (manage_staff_access or manage_team) posts to
// /admin-review/people; false (manage_staff_profiles only) posts to the
// existing roster-only /admin-review/staff route (widened to admit that
// capability) with the login section rendered read-only, "An owner will
// grant the login" — an actor without the capability to grant a login
// never sends login fields at all, rather than sending them and trusting
// the server to ignore them.
//
// The company control renders for the OWNER only (whoami.role, mirroring
// the server's field-level role gate) — absent for everyone else, never
// disabled.
// =====================================================================

/** The designation the form preselects — the most common new hire. */
const DEFAULT_ADD_PERSON_ROLE: BillableRole = 'care_manager';

function AddPersonSheet({
  isOpen,
  tenantSlug,
  canGrantLogin,
  showCompanyControl,
  privateContactVisible,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  tenantSlug: string;
  /** manage_staff_access or manage_team — whether this save may reach the
   *  login-granting route at all. Forces grantLogin off and renders the
   *  login section read-only when false. */
  canGrantLogin: boolean;
  /** whoami.role === 'owner'. The server rejects a non-owner company_id
   *  with company_override_forbidden regardless — UI absence is not the
   *  authority, just the honest rendering of it. */
  showCompanyControl: boolean;
  /** Whether the list the page loaded served the private pair
   *  (private_contact_visible): the home address and personal phone
   *  fields render only then, and the server refuses the write otherwise
   *  (the DirectoryFieldsEditor rule). */
  privateContactVisible: boolean;
  onClose: () => void;
  onSuccess: (message: string) => Promise<void>;
}): ReactNode {
  const optionsQuery = useQuery<StaffCreateOptionsResponse, Error>({
    queryKey: ['admin-review', 'staff-create-options', tenantSlug],
    queryFn: () => staffAdminApi.createOptions(tenantSlug),
    enabled: isOpen,
    staleTime: STALE_MS,
  });

  const [fullName, setFullName] = useState('');
  const [designation, setDesignation] = useState<BillableRole>(DEFAULT_ADD_PERSON_ROLE);
  const [employmentType, setEmploymentType] = useState<'employee' | 'contractor'>('employee');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [credentials, setCredentials] = useState('');
  const [unitId, setUnitId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [targetHours, setTargetHours] = useState('');
  // The employee-card fields, so the person arrives with their card filled.
  const [birthdayMonth, setBirthdayMonth] = useState('');
  const [birthdayDay, setBirthdayDay] = useState('');
  const [workAnniversary, setWorkAnniversary] = useState('');
  const [startDate, setStartDate] = useState('');
  const [personalPhone, setPersonalPhone] = useState('');
  // The login section: on by default when this actor may grant one at all.
  const [grantLogin, setGrantLogin] = useState(canGrantLogin);
  // The access role is always the operator's own choice: nothing fills it
  // in from the designation, and the form will not save a login without it.
  const [accessRole, setAccessRole] = useState<UserTenantRole | ''>('');
  const contact = useSupportContact();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // name_taken_archived — the archived row's id, so the form offers the
  // one-click Restore instead of a dead-end error (spec §8.2).
  const [archivedConflictId, setArchivedConflictId] = useState<string | null>(null);
  // The Directory Editor door's confirm for a returning person: the save it
  // refused, and the number of clients she loses access to.
  const [careTeamConfirm, setCareTeamConfirm] = useState<{ input: CreatePersonRequest; count: number } | null>(null);
  // A Directory Editor does no client work, so she is added as Admin: the
  // designation follows the access and stays put while it is chosen.
  const designationLocked = isAddedAsAdmin(accessRole);

  useEffect(() => {
    if (!isOpen) {
      setFullName('');
      setDesignation(DEFAULT_ADD_PERSON_ROLE);
      setEmploymentType('employee');
      setEmail('');
      setPhone('');
      setCredentials('');
      setUnitId('');
      setCompanyId('');
      setTargetHours('');
      setBirthdayMonth('');
      setBirthdayDay('');
      setWorkAnniversary('');
      setStartDate('');
      homeForm.reset(null);
      setPersonalPhone('');
      setGrantLogin(canGrantLogin);
      setAccessRole('');
      setErrorMessage(null);
      setArchivedConflictId(null);
      setCareTeamConfirm(null);
    }
  }, [isOpen, canGrantLogin]);

  const changeDesignation = (next: BillableRole): void => {
    setDesignation(next);
  };
  const changeAccessRole = (next: UserTenantRole | ''): void => {
    setAccessRole(next);
    if (isAddedAsAdmin(next)) setDesignation('admin');
  };

  // Prefill the owner's company control with the configured operating
  // company — the common case is to leave it alone.
  const defaultCompanyId = optionsQuery.data?.default_company_id ?? null;
  useEffect(() => {
    if (isOpen && companyId === '' && defaultCompanyId !== null) setCompanyId(defaultCompanyId);
  }, [isOpen, companyId, defaultCompanyId]);

  const handleConflict = (err: unknown): boolean => {
    if (err instanceof ApiError && errorCodeOf(err) === 'name_taken_archived') {
      const rosterId = (err.body as { roster_id?: string }).roster_id;
      if (rosterId) {
        setArchivedConflictId(rosterId);
        setErrorMessage(null);
        return true;
      }
    }
    return false;
  };
  const messageFor = (err: unknown): string => peopleRefusalText(err, contact);

  // Login-granting save (canGrantLogin): writes the roster row and, when
  // the login section is on, the invite — one transaction on the server.
  const withMapNote = (added: string, outcome: HomePointOutcome | undefined): string => {
    const note = homePointAddedNote(outcome);
    return note ? `${added} ${note}` : added;
  };
  const createPersonMutation = useMutation({
    mutationFn: (input: CreatePersonRequest) => peopleAdminApi.create(tenantSlug, input),
    onSuccess: (result) =>
      void onSuccess(
        withMapNote(
          result.pending_grant
            ? `Added ${result.row.full_name} to Olive. Olive doesn't email them: send them the link, and their login works the first time they sign in as ${result.pending_grant.email}.`
            : `Added ${result.row.full_name} to Olive.`,
          result.home_point,
        ),
      ),
    onError: (err, input) => {
      if (handleConflict(err)) return;
      const count = careTeamsToConfirm(err);
      if (count !== null) {
        setCareTeamConfirm({ input, count });
        return;
      }
      setErrorMessage(messageFor(err));
    },
  });

  // Roster-only save (profiles-only actor): the existing route, never
  // touches a login.
  const createStaffMutation = useMutation({
    mutationFn: (input: CreateStaffRequest) => staffAdminApi.create(tenantSlug, input),
    onSuccess: (result) =>
      void onSuccess(withMapNote(`Added ${result.row.full_name} to the roster.`, result.home_point)),
    onError: (err) => {
      if (handleConflict(err)) return;
      setErrorMessage(messageFor(err));
    },
  });

  // The row and the name the sentence uses are read at the press.
  const restoreConflictMutation = useMutation({
    mutationFn: (v: { rosterId: string; name: string }) => staffAdminApi.restore(tenantSlug, v.rosterId),
    onSuccess: (_res, v) => void onSuccess(endSentence(`Restored ${v.name}`)),
    onError: (err) => setErrorMessage(messageFor(err)),
  });

  const busy =
    createPersonMutation.isPending || createStaffMutation.isPending || restoreConflictMutation.isPending;

  // The home is the shared address form (a street, town, state and ZIP),
  // checked like every other address before the person is added; it is
  // offered, and sent, only to a seat that may read it.
  const homeForm = useAddressForm({
    existing: null,
    rule: 'home',
    idPrefix: 'add-person',
    enabled: privateContactVisible,
    done: false,
    saving: busy,
    onSave: (parts) => send(parts),
  });

  /** The form's own checks; false when one names a gap. */
  const formReady = (): boolean => {
    const name = fullName.trim();
    if (name.length === 0) {
      setErrorMessage('Enter their full name.');
      return false;
    }
    const wantsLogin = canGrantLogin && grantLogin;
    if (wantsLogin && email.trim().length === 0) {
      setErrorMessage('Enter their email to give them a login.');
      return false;
    }
    if (wantsLogin && accessRole === '') {
      setErrorMessage('Choose the access their login should have.');
      return false;
    }
    const birthday = birthdayPairFromText(birthdayMonth, birthdayDay);
    if (birthday.kind === 'incomplete') {
      setErrorMessage(BIRTHDAY_PAIR_MESSAGE);
      return false;
    }
    return true;
  };

  const submit = (): void => {
    if (!formReady()) return;
    // A typed home goes through the address form (its gaps, then the
    // check), which sends with the parts; an empty one sends none.
    if (privateContactVisible && !homeForm.isEmpty()) {
      homeForm.pressSave();
      return;
    }
    send(null);
  };

  const send = (home: AddressFormParts | null): void => {
    const name = fullName.trim();
    const wantsLogin = canGrantLogin && grantLogin;
    const birthday = birthdayPairFromText(birthdayMonth, birthdayDay);
    if (birthday.kind === 'incomplete') return;
    setErrorMessage(null);
    setArchivedConflictId(null);

    const shared = {
      full_name: name,
      billable_role: designation,
      employment_type: employmentType,
      ...(email.trim().length > 0 ? { email: email.trim() } : {}),
      ...(phone.trim().length > 0 ? { phone: phone.trim() } : {}),
      ...(credentials.trim().length > 0 ? { credentials: credentials.trim() } : {}),
      ...(unitId !== '' ? { unit_id: unitId } : {}),
      // OWNER ONLY — a non-owner request never carries the field at all
      // (the server rejects it with 403 rather than dropping it).
      ...(showCompanyControl && companyId !== '' ? { company_id: companyId } : {}),
      ...(targetHours.trim().length > 0 ? { target_weekly_hours: targetHours.trim() } : {}),
      // The card fields ride only when filled in; absent means nothing on
      // file, the same as leaving the card blank after the fact.
      ...(birthday.kind === 'set' ? { birthday_month: birthday.month, birthday_day: birthday.day } : {}),
      ...(workAnniversary !== '' ? { work_anniversary: workAnniversary } : {}),
      ...(startDate !== '' ? { start_date: startDate } : {}),
      ...(privateContactVisible && home !== null ? { home_address: home } : {}),
      ...(privateContactVisible && personalPhone.trim().length > 0 ? { personal_phone: personalPhone.trim() } : {}),
    };

    if (canGrantLogin) {
      const input: CreatePersonRequest = {
        ...shared,
        grant_login: wantsLogin,
        ...(wantsLogin && accessRole !== '' ? { access_role: accessRole } : {}),
      };
      createPersonMutation.mutate(input);
    } else {
      createStaffMutation.mutate(shared as CreateStaffRequest);
    }
  };

  const units = optionsQuery.data?.units ?? [];
  const companies = optionsQuery.data?.companies ?? [];
  const companyUnconfigured =
    showCompanyControl && optionsQuery.isSuccess && defaultCompanyId === null;

  return (
    <>
    {careTeamConfirm !== null && (
      <CareTeamConfirmSheet
        name={careTeamConfirm.input.full_name}
        count={careTeamConfirm.count}
        outcome="Add person"
        busy={createPersonMutation.isPending}
        onClose={() => setCareTeamConfirm(null)}
        onConfirm={() => {
          const confirmed = careTeamConfirm;
          setCareTeamConfirm(null);
          createPersonMutation.mutate({ ...confirmed.input, end_care_teams: confirmed.count });
        }}
      />
    )}
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Add a person">
      <div className="p-3 space-y-3">
        <p className="text-sm text-ink-soft">
          {canGrantLogin
            ? 'One save adds their roster record and, unless you turn it off below, their sign-in.'
            : 'Adds them to the roster. An owner grants the login separately.'}
        </p>

        {errorMessage && (
          <div
            role="alert"
            className="p-3 border border-pepper/40 bg-pepper-soft rounded-md text-sm text-pepper-deep"
            data-testid="add-person-error"
          >
            {errorMessage}
          </div>
        )}

        {archivedConflictId !== null ? (
          <div
            className="p-3 border border-warm-300 bg-warm-50 rounded-md space-y-2"
            data-testid="add-person-archived-conflict"
          >
            <p className="text-sm text-ink">
              There is already a former staff member with this name. Restore them instead of
              adding a duplicate?
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setArchivedConflictId(null)}
                className="min-h-9 px-3 py-1.5 text-sm text-ink-soft hover:text-sage-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => restoreConflictMutation.mutate({ rosterId: archivedConflictId, name: fullName.trim() })}
                data-testid="add-person-restore"
                className="min-h-9 px-3 py-1.5 text-sm bg-sage-700 text-cream rounded-md hover:bg-sage-800 disabled:opacity-50"
              >
                {restoreConflictMutation.isPending ? 'Restoring…' : 'Restore them'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div>
              <label htmlFor="add-person-name" className={FLD_LABEL}>
                Full name
              </label>
              <input
                id="add-person-name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jordan Lee"
                data-testid="add-person-name"
                className={FLD}
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="add-person-designation" className={FLD_LABEL}>
                  What do they do
                </label>
                <SelectField size="field"
                  id="add-person-designation"
                  value={designation}
                  disabled={designationLocked}
                  onChange={(e) => changeDesignation(e.target.value as BillableRole)}
                  data-testid="add-person-designation"
                  className="w-full"
                >
                  {BILLABLE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {billableRoleLabel(r)}
                    </option>
                  ))}
                </SelectField>
                {designationLocked && <p className="mt-1 text-xs text-ink-soft">{ADDED_AS_ADMIN_LINE}</p>}
              </div>
              <div>
                <label htmlFor="add-person-employment" className={FLD_LABEL}>
                  Employment type
                </label>
                <SelectField size="field"
                  id="add-person-employment"
                  value={employmentType}
                  onChange={(e) => setEmploymentType(e.target.value as 'employee' | 'contractor')}
                  data-testid="add-person-employment"
                  className="w-full"
                >
                  <option value="employee">Employee</option>
                  <option value="contractor">Contractor</option>
                </SelectField>
              </div>
            </div>

            {/* The company control — OWNER ONLY, prefilled with the
                configured operating company; labelled for what it does,
                not for the exception it enables. */}
            {showCompanyControl && (
              <div>
                <label htmlFor="add-person-company" className={FLD_LABEL}>
                  Company
                </label>
                <SelectField size="field"
                  id="add-person-company"
                  value={companyId}
                  onChange={(e) => setCompanyId(e.target.value)}
                  data-testid="add-person-company"
                  className="w-full"
                >
                  {companyId === '' && <option value="">Choose a company…</option>}
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </SelectField>
                {companyUnconfigured && (
                  <p className="mt-1 text-xs text-caution-deep" role="alert">
                    {contactSentence("No company is set up yet, so there's nothing to pick here. Ask {contact_admin} to finish the setup.", contact)}
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="add-person-unit" className={FLD_LABEL}>
                  Unit
                </label>
                <SelectField size="field"
                  id="add-person-unit"
                  value={unitId}
                  onChange={(e) => setUnitId(e.target.value)}
                  data-testid="add-person-unit"
                  className="w-full"
                >
                  <option value="">No unit</option>
                  {units.map((u) => (
                    <option key={u.id} value={u.id}>
                      {unitOptionLabel(u)}
                    </option>
                  ))}
                </SelectField>
              </div>
              {/* The weekly hours target, for every designation: a SPICE
                  session is one hour, so a therapist's target is hours too. */}
              <div>
                <label htmlFor="add-person-target-hours" className={FLD_LABEL}>
                  Target weekly hours
                </label>
                <input
                  id="add-person-target-hours"
                  type="text"
                  inputMode="decimal"
                  value={targetHours}
                  onChange={(e) => setTargetHours(e.target.value)}
                  placeholder="20"
                  data-testid="add-person-target-hours"
                  className={FLD}
                />
              </div>
            </div>

            <p className="text-xs font-medium text-ink-soft pt-1">Optional</p>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="add-person-email" className={FLD_LABEL}>
                  Email
                </label>
                <input
                  id="add-person-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jordan@example.com"
                  data-testid="add-person-email"
                  className={FLD}
                />
              </div>
              <div>
                <label htmlFor="add-person-phone" className={FLD_LABEL}>
                  Direct phone
                </label>
                <input
                  id="add-person-phone"
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="The number families can call"
                  data-testid="add-person-phone"
                  className={FLD}
                />
              </div>
            </div>

            <div>
              <label htmlFor="add-person-credentials" className={FLD_LABEL}>
                Credentials
              </label>
              <input
                id="add-person-credentials"
                type="text"
                value={credentials}
                onChange={(e) => setCredentials(e.target.value)}
                placeholder="BSW, CSW, CMC"
                data-testid="add-person-credentials"
                className={FLD}
              />
            </div>

            {/* The employee-card fields: what their card on the People page
                and the Team directory shows, taken here so nobody has to
                open the card afterwards to fill it in. */}
            <p className="text-xs font-medium text-ink-soft pt-1">Details for their card</p>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="add-person-birthday-month" className={FLD_LABEL}>
                  Birthday
                </label>
                <div className="flex gap-2">
                  <SelectField size="field"
                    id="add-person-birthday-month"
                    value={birthdayMonth}
                    onChange={(e) => setBirthdayMonth(e.target.value)}
                    aria-label="Birthday month"
                    data-testid="add-person-birthday-month"
                    className="w-full flex-1"
                  >
                    <option value="">Month</option>
                    {MONTH_OPTIONS.map((label, i) => (
                      <option key={label} value={String(i + 1)}>{label}</option>
                    ))}
                  </SelectField>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={birthdayDay}
                    onChange={(e) => setBirthdayDay(e.target.value)}
                    placeholder="Day"
                    aria-label="Birthday day"
                    data-testid="add-person-birthday-day"
                    className={`${FLD} w-24`}
                  />
                </div>
                <p className="mt-1 text-xs text-ink-soft">Month and day only; no year is kept.</p>
              </div>
              <div>
                <label htmlFor="add-person-start-date" className={FLD_LABEL}>
                  Start date
                </label>
                <DateInput
                  id="add-person-start-date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="add-person-start-date"
                  size="field" className="w-full"
                />
              </div>
              <div>
                <label htmlFor="add-person-work-anniversary" className={FLD_LABEL}>
                  Work anniversary
                </label>
                <DateInput
                  id="add-person-work-anniversary"
                  value={workAnniversary}
                  onChange={(e) => setWorkAnniversary(e.target.value)}
                  data-testid="add-person-work-anniversary"
                  size="field" className="w-full"
                />
                <p className="mt-1 text-xs text-ink-soft">Leave blank to count from the start date.</p>
              </div>
              {privateContactVisible && (
                <div>
                  <label htmlFor="add-person-personal-phone" className={FLD_LABEL}>
                    Personal phone
                  </label>
                  <input
                    id="add-person-personal-phone"
                    type="text"
                    value={personalPhone}
                    onChange={(e) => setPersonalPhone(e.target.value)}
                    placeholder={PHONE_EXAMPLE}
                    data-testid="add-person-personal-phone"
                    className={FLD}
                  />
                </div>
              )}
            </div>

            {privateContactVisible && (
              <fieldset className="flex min-w-0 flex-col gap-2">
                <legend className={FLD_LABEL}>Home address</legend>
                <AddressFields idPrefix="add-person" {...homeForm.fields} />
                <AddressCheckPrompt phase={homeForm.check.phase} />
                {homeForm.prompting && (
                  <AddressCheckChoices
                    phase={homeForm.check.phase}
                    busy={busy}
                    onKeep={() => homeForm.choose('checked', false)}
                    onUse={() => homeForm.choose('checked', true)}
                    onSaveAsTyped={() => homeForm.choose('saved_as_typed', false)}
                  />
                )}
                <p className="text-xs text-ink-soft">
                  The map places them from this address. Home address and personal phone show only
                  to the office.
                </p>
              </fieldset>
            )}

            {/* The login section — the second half of the one door. On by
                default; read-only for an actor who cannot grant a login at
                all, who saves through the roster-only route instead. */}
            <div className="rounded-xl border border-sage-400/20 bg-sand/40 p-3 space-y-2">
              {canGrantLogin ? (
                <>
                  <label className="flex items-center gap-2 text-sm text-ink cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={grantLogin}
                      onChange={(e) => setGrantLogin(e.target.checked)}
                      data-testid="add-person-grant-login"
                      className="h-[16px] w-[16px] accent-[var(--color-sage-800)]"
                    />
                    Give them a login
                  </label>
                  {grantLogin && (
                    <div>
                      <label htmlFor="add-person-access-role" className={FLD_LABEL}>
                        Access
                      </label>
                      <SelectField size="field"
                        id="add-person-access-role"
                        value={accessRole}
                        onChange={(e) => changeAccessRole(e.target.value as UserTenantRole | '')}
                        data-testid="add-person-access-role"
                        className="w-full"
                      >
                        <option value="">Choose…</option>
                        {TEAM_GRANTABLE_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {accessRoleLabel(r)}
                          </option>
                        ))}
                      </SelectField>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-ink-soft" data-testid="add-person-login-readonly">
                  An owner will grant the login.
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="min-h-11 px-4 py-2 text-sm text-ink-soft hover:text-sage-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy || (canGrantLogin && grantLogin && accessRole === '')}
                onClick={submit}
                data-testid="add-person-submit"
                className="min-h-11 px-4 py-2 text-sm bg-sage-700 text-cream rounded-md hover:bg-sage-800 disabled:opacity-50"
              >
                {busy ? 'Adding…' : 'Add to Olive'}
              </button>
            </div>
          </>
        )}
      </div>
    </BottomSheet>
    </>
  );
}

// =====================================================================
// ArchiveConfirmSheet — the "Remove from Olive" confirm with the departure
// warning (founder-ruled 2026-07-26: warn with counts, never block,
// never auto-reassign). The pre-flight is fetched when the confirm
// OPENS; it is advisory — removal proceeds regardless, and the confirm
// never disables on the counts (or on a failed check).
// =====================================================================

/** One count sentence with its expandable list ("See list" — the operator
 *  is mid-decision; no navigation away from the confirm). */
function PreflightCountLine({
  label,
  items,
  testId,
}: {
  label: string;
  items: string[];
  testId: string;
}): ReactNode {
  return (
    <details data-testid={testId}>
      <summary className="cursor-pointer text-sm text-ink">
        <strong>{label}</strong> <span className="text-xs text-sage-700">See list</span>
      </summary>
      <ul className="mt-1 ml-5 list-disc text-xs text-ink-soft">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </details>
  );
}

function ArchiveConfirmSheet({
  tenantSlug,
  row,
  hasPendingInvite,
  busy,
  onClose,
  onConfirm,
}: {
  tenantSlug: string;
  row: PersonRow;
  /** A queued invite for this row's own email — revoked in the same act,
   *  named here so the confirm says everything it is about to do. */
  hasPendingInvite: boolean;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}): ReactNode {
  const rosterId = row.roster_id;
  const preflightQuery = useQuery<ArchivePreflightResponse, Error>({
    queryKey: ['admin-review', 'staff-archive-preflight', tenantSlug, rosterId],
    queryFn: () => staffAdminApi.archivePreflight(tenantSlug, rosterId as string),
    enabled: rosterId !== null,
    // Always fresh — the operator is about to act on these numbers.
    staleTime: 0,
    gcTime: 0,
  });
  const name = personName(row);
  const clients = preflightQuery.data?.clients ?? [];
  const primaryClients = clients.filter((c) => c.is_primary_cm);
  const spiceClients = clients.filter((c) => c.is_spice_therapist);
  const sharedCareClients = clients.filter((c) => c.is_additional_cm);
  const sharedSpiceClients = clients.filter((c) => c.is_additional_spice_therapist);
  const sessionClients = clients.filter((c) => c.is_spice_session_therapist);
  const openCards = preflightQuery.data?.open_work_items ?? [];
  const hasCounts =
    primaryClients.length > 0 ||
    spiceClients.length > 0 ||
    sharedCareClients.length > 0 ||
    sharedSpiceClients.length > 0 ||
    sessionClients.length > 0 ||
    openCards.length > 0;
  const loginActive = row.user_id !== null && row.account_is_active !== false;

  return (
    <BottomSheet isOpen={true} onClose={onClose} title={`Remove ${name} from Access & Information?`}>
      <div className="p-3 space-y-3">
        <p className="text-sm text-ink">
          They move to Former staff; their past work and audit trail are kept.
        </p>

        {preflightQuery.isLoading && (
          <p className="text-xs text-ink-soft" data-testid="archive-preflight-loading">
            Checking their assignments…
          </p>
        )}
        {preflightQuery.isError && (
          <p className="text-xs text-ink-soft" data-testid="archive-preflight-error">
            Their clients couldn't be checked just now. Close this and open it again to check, or remove them anyway.
          </p>
        )}

        {hasCounts && (
          <div
            className="p-3 border border-warm-300 bg-warm-50 rounded-md space-y-2"
            data-testid="archive-preflight"
          >
            {primaryClients.length > 0 && (
              <PreflightCountLine
                testId="archive-preflight-primary"
                label={`Primary care manager on ${primaryClients.length} ${primaryClients.length === 1 ? 'client' : 'clients'}.`}
                items={primaryClients.map((c) => c.display_name)}
              />
            )}
            {spiceClients.length > 0 && (
              <PreflightCountLine
                testId="archive-preflight-spice"
                label={`SPICE therapist for ${spiceClients.length} ${spiceClients.length === 1 ? 'client' : 'clients'}.`}
                items={spiceClients.map((c) => c.display_name)}
              />
            )}
            {sharedCareClients.length > 0 && (
              <PreflightCountLine
                testId="archive-preflight-additional-cm"
                label={`Shares ${sharedCareClients.length} ${sharedCareClients.length === 1 ? 'client' : 'clients'} as an additional care manager.`}
                items={sharedCareClients.map((c) => c.display_name)}
              />
            )}
            {sharedSpiceClients.length > 0 && (
              <PreflightCountLine
                testId="archive-preflight-additional-spice"
                label={`Shares ${sharedSpiceClients.length} ${sharedSpiceClients.length === 1 ? 'client' : 'clients'} as an additional SPICE therapist.`}
                items={sharedSpiceClients.map((c) => c.display_name)}
              />
            )}
            {sessionClients.length > 0 && (
              <PreflightCountLine
                testId="archive-preflight-spice-sessions"
                label={`SPICE sessions on ${sessionClients.length} ${sessionClients.length === 1 ? 'client' : 'clients'}. Those sessions go back to each client's SPICE therapist.`}
                items={sessionClients.map((c) => c.display_name)}
              />
            )}
            {openCards.length > 0 && (
              <PreflightCountLine
                testId="archive-preflight-cards"
                label={`Owns ${openCards.length} open ${openCards.length === 1 ? 'card' : 'cards'}.`}
                items={openCards.map((w) => w.title)}
              />
            )}
            <p className="text-xs text-ink-soft">
              These keep pointing at {name} after removal and will need reassigning. Nothing is
              reassigned automatically.
            </p>
          </div>
        )}

        {loginActive && (
          <p className="text-sm text-ink">
            Signing in will be turned off
            {row.account_email ? (
              <>
                {' '}for <span className="font-mono break-all">{row.account_email}</span>
              </>
            ) : null}
            .
          </p>
        )}

        {hasPendingInvite && (
          <p className="text-sm text-ink" data-testid="archive-revokes-pending-invite">
            Their login waiting for a first sign-in is removed too.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 px-4 py-2 text-sm text-ink-soft hover:text-sage-800"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="min-h-11 px-4 py-2 text-sm text-cream rounded-md disabled:opacity-50 bg-pepper hover:brightness-[0.96]"
          >
            {busy ? 'Working…' : 'Remove from Olive'}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

// =====================================================================
// ConfirmSheet (remove / reactivate / revoke)
// =====================================================================

function ConfirmSheet({
  title,
  body,
  confirmLabel,
  confirmVariant,
  busy,
  onClose,
  onConfirm,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  confirmVariant: 'sage' | 'danger';
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}): ReactNode {
  const confirmClass =
    confirmVariant === 'danger' ? 'bg-pepper hover:brightness-[0.96]' : 'bg-sage-700 hover:bg-sage-800';
  return (
    <BottomSheet isOpen={true} onClose={onClose} title={title}>
      <div className="p-3 space-y-3">
        <p className="text-sm text-ink">{body}</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="min-h-11 px-4 py-2 text-sm text-ink-soft hover:text-sage-800">
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className={`min-h-11 px-4 py-2 text-sm text-cream rounded-md disabled:opacity-50 ${confirmClass}`}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </BottomSheet>
  );
}

// =====================================================================
// BulkAddSheet — add team members (paste → preview → commit)
// =====================================================================

type BulkStage = 'form' | 'preview';

/** One draft person in the structured Add-people form. */
export interface PersonDraft {
  email: string;
  name: string;
  role: UserTenantRole;
}

/**
 * Default access role for a fresh row. Primary CM is the most common
 * grant (front-line care managers); the owner can change it per row.
 */
const DEFAULT_DRAFT_ROLE: UserTenantRole = 'primary_cm';

// Basic email shape, mirrors api/_shared/teamBulkAdd/parser.ts EMAIL_RE so a
// typo is caught inline before the preview round-trip. Not RFC-5322.
const DRAFT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function emptyDraft(): PersonDraft {
  return { email: '', name: '', role: DEFAULT_DRAFT_ROLE };
}

/** A row the operator has begun filling in (email or name has content). */
function isStartedDraft(d: PersonDraft): boolean {
  return d.email.trim().length > 0 || d.name.trim().length > 0;
}

/**
 * Per-row validation message, or null when the row is fine. Untouched rows
 * (all blank) never error, so a spare empty slot doesn't block submit.
 */
export function draftRowError(d: PersonDraft): string | null {
  if (!isStartedDraft(d)) return null;
  const email = d.email.trim();
  if (email.length === 0) return 'Email is required.';
  if (!DRAFT_EMAIL_RE.test(email)) return 'Enter a valid email address.';
  return null;
}

/**
 * Assemble the structured drafts into the `email,role,name` paste the
 * preview/commit endpoints already parse (parser.ts). Blank rows are
 * dropped; commas in the name are flattened to spaces so the line stays
 * a clean 3-field record. The server contract is unchanged; the form just
 * produces clean role tokens instead of hand-typed ones.
 */
export function buildBulkAddPaste(drafts: PersonDraft[]): string {
  return drafts
    .filter((d) => d.email.trim().length > 0)
    .map((d) => {
      const email = d.email.trim();
      const name = d.name.replace(/,/g, ' ').replace(/\s+/g, ' ').trim();
      return name.length > 0 ? `${email},${d.role},${name}` : `${email},${d.role}`;
    })
    .join('\n');
}

function BulkAddSheet({
  isOpen,
  tenantSlug,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  tenantSlug: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
}): ReactNode {
  const queryClient = useQueryClient();
  const [stage, setStage] = useState<BulkStage>('form');
  const [drafts, setDrafts] = useState<PersonDraft[]>([emptyDraft()]);
  const [preview, setPreview] = useState<BulkAddPreviewResponse | null>(null);
  const contact = useSupportContact();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setStage('form');
      setDrafts([emptyDraft()]);
      setPreview(null);
      setErrorMessage(null);
    }
  }, [isOpen]);

  const previewMutation = useMutation({
    mutationFn: (paste: string) => teamApi.previewBulkAdd(tenantSlug, paste),
    onSuccess: (data) => {
      setPreview(data);
      setStage('preview');
      setErrorMessage(null);
    },
    onError: (err) => setErrorMessage(peopleRefusalText(err, contact)),
  });

  // The rows committed are the preview on screen at the press.
  const commitMutation = useMutation({
    mutationFn: (rows: Parameters<typeof teamApi.commitBulkAdd>[1]['rows'] | null) => {
      if (rows === null) throw new Error('Read the paste first.');
      return teamApi.commitBulkAdd(tenantSlug, { rows });
    },
    onSuccess: async (result) => {
      const parts: string[] = [];
      if (result.granted_existing) parts.push(`Granted ${result.granted_existing}`);
      if (result.queued_pending) parts.push(`Logins ready: ${result.queued_pending}`);
      if (result.already_member) parts.push(`${result.already_member} already a member`);
      if (result.already_queued) parts.push(`${result.already_queued} already had a login waiting`);
      const msg = parts.length === 0 ? 'No changes.' : parts.join('; ') + '.';
      await invalidatePeople(queryClient, tenantSlug);
      onSuccess(msg);
    },
    onError: (err) => {
      setErrorMessage(peopleRefusalText(err, contact));
      setStage('preview');
    },
  });

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Give sign-in access">
      <div className="p-3 space-y-3">
        {errorMessage && (
          <div
            role="alert"
            className="p-3 border border-pepper/40 bg-pepper-soft rounded-md text-sm text-pepper-deep"
            data-testid="bulk-access-error"
          >
            {errorMessage}
          </div>
        )}

        {stage === 'form' && (
          <AddPeopleForm
            drafts={drafts}
            onChange={setDrafts}
            busy={previewMutation.isPending}
            onCancel={onClose}
            onSubmit={(ready) => previewMutation.mutate(buildBulkAddPaste(ready))}
          />
        )}

        {stage === 'preview' && preview && (
          <>
            <div className="text-sm text-ink space-y-1">
              <div>
                Will add now: <strong>{preview.summary.will_grant}</strong>
                {' · '}New logins: <strong>{preview.summary.will_queue}</strong>
              </div>
              {(preview.summary.already_member > 0 ||
                preview.summary.already_queued > 0 ||
                preview.summary.parse_error > 0) && (
                <div className="text-ink-soft text-xs">
                  Skipped: {preview.summary.already_member} already a member ·{' '}
                  {preview.summary.already_queued} already waiting · {preview.summary.parse_error} errors
                </div>
              )}
            </div>
            <div className="border border-sage-800/[8%] rounded-md divide-y divide-sage-800/[8%] max-h-64 overflow-y-auto">
              {preview.rows.map((r) => (
                <div key={r.line_no} className="px-3 py-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-ink-soft">L{r.line_no}</span>
                    <span className={statusBadgeClass(r.status)}>{statusLabel(r.status)}</span>
                  </div>
                  {r.email && (
                    <div className="text-ink mt-0.5">
                      {r.email}
                      {r.role && ` → ${displayRole(r.role)}`}
                      {r.resolved_display_name && ` (${r.resolved_display_name})`}
                    </div>
                  )}
                  {r.error_message && <div className="text-pepper-deep mt-0.5">{r.error_message}</div>}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setStage('form')} className="min-h-11 px-4 py-2 text-sm text-ink-soft hover:text-sage-800">
                Back
              </button>
              <button
                type="button"
                disabled={commitMutation.isPending || preview.summary.will_grant + preview.summary.will_queue === 0}
                onClick={() =>
                  commitMutation.mutate(
                    preview
                      ? preview.rows
                          .filter((r) => r.status === 'will_grant' || r.status === 'will_queue')
                          .filter((r) => r.email !== null && r.role !== null)
                          .map((r) => ({ email: r.email!, role: r.role!, display_name: r.display_name }))
                      : null,
                  )
                }
                className="min-h-11 px-4 py-2 text-sm bg-sage-700 text-cream rounded-md hover:bg-sage-800 disabled:opacity-50"
              >
                {commitMutation.isPending ? 'Adding…' : 'Add'}
              </button>
            </div>
          </>
        )}
      </div>
    </BottomSheet>
  );
}

/**
 * AddPeopleForm — the structured, repeatable Add-people rows (X redesign).
 *
 * One row per person: Email + Name (optional) + a Role SELECT. The role is
 * PICKED from a dropdown driven by TEAM_GRANTABLE_ROLES → ROLE_DISPLAY, so a
 * token is never typed (the "board_observer" free-text error that motivated
 * this change is structurally impossible now). Controlled by the parent sheet
 * so rows survive a Back trip from the preview stage.
 *
 * On submit it hands the started rows up; the parent assembles them into the
 * paste the preview/commit endpoints already parse (buildBulkAddPaste). A row
 * with a name but no email is caught here and never reaches the server.
 */
export function AddPeopleForm({
  drafts,
  onChange,
  busy,
  onSubmit,
  onCancel,
}: {
  drafts: PersonDraft[];
  onChange: (next: PersonDraft[]) => void;
  busy: boolean;
  onSubmit: (ready: PersonDraft[]) => void;
  onCancel: () => void;
}): ReactNode {
  const [showErrors, setShowErrors] = useState(false);

  const started = drafts.filter(isStartedDraft);
  const hasEmail = started.some((d) => d.email.trim().length > 0);
  const anyRowError = drafts.some((d) => draftRowError(d) !== null);

  const updateRow = (i: number, patch: Partial<PersonDraft>): void =>
    onChange(drafts.map((d, j) => (j === i ? { ...d, ...patch } : d)));

  const addRow = (): void => onChange([...drafts, emptyDraft()]);

  const removeRow = (i: number): void => {
    const next = drafts.filter((_, j) => j !== i);
    onChange(next.length === 0 ? [emptyDraft()] : next);
    setShowErrors(false);
  };

  const handleSubmit = (): void => {
    setShowErrors(true);
    if (busy || !hasEmail || anyRowError) return;
    onSubmit(started);
  };

  return (
    <>
      <p className="text-sm text-ink-soft">
        Grants a login: who can open Olive, and as what. It does not put anyone on the staff
        roster (add them there separately with “+ Add a person” if they are not on it yet).
      </p>

      <div className="space-y-3">
        {drafts.map((d, i) => {
          const rowError = showErrors ? draftRowError(d) : null;
          const personNo = i + 1;
          return (
            <div key={i} className="rounded-xl border border-sage-400/20 bg-sand/40 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[12.5px] font-semibold text-sage-400 tracking-[0.01em]">
                  Person {personNo}
                </span>
                {drafts.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    aria-label={`Remove person ${personNo}`}
                    className="w-8 h-8 inline-flex items-center justify-center rounded-full text-ink-soft hover:text-pepper-deep hover:bg-pepper-soft transition-colors"
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor={`add-email-${i}`} className={FLD_LABEL}>
                    Email
                  </label>
                  <input
                    id={`add-email-${i}`}
                    type="email"
                    value={d.email}
                    onChange={(e) => updateRow(i, { email: e.target.value })}
                    placeholder="jordan@example.com"
                    aria-label={`Email for person ${personNo}`}
                    className={FLD}
                  />
                </div>
                <div>
                  <label htmlFor={`add-name-${i}`} className={FLD_LABEL}>
                    Name <span className="font-normal text-ink-soft">(optional)</span>
                  </label>
                  <input
                    id={`add-name-${i}`}
                    type="text"
                    value={d.name}
                    onChange={(e) => updateRow(i, { name: e.target.value })}
                    placeholder="Jordan Lee"
                    aria-label={`Name for person ${personNo}`}
                    className={FLD}
                  />
                </div>
              </div>

              <div>
                <label htmlFor={`add-role-${i}`} className={FLD_LABEL}>
                  Role
                </label>
                <SelectField size="field"
                  id={`add-role-${i}`}
                  value={d.role}
                  onChange={(e) => updateRow(i, { role: e.target.value as UserTenantRole })}
                  aria-label={`Role for person ${personNo}`}
                  className="w-full"
                >
                  {TEAM_GRANTABLE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROLE_DISPLAY[r]}
                    </option>
                  ))}
                </SelectField>
              </div>

              {rowError && (
                <p role="alert" className="text-xs text-pepper-deep">
                  {rowError}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={addRow}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-sage-700 hover:text-sage-800"
      >
        <Plus size={16} aria-hidden="true" /> Add another
      </button>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="min-h-11 px-4 py-2 text-sm text-ink-soft hover:text-sage-800">
          Cancel
        </button>
        <button
          type="button"
          disabled={busy || started.length === 0}
          onClick={handleSubmit}
          className="min-h-11 px-4 py-2 text-sm bg-sage-700 text-cream rounded-md hover:bg-sage-800 disabled:opacity-50"
        >
          {busy ? 'Checking…' : 'Preview'}
        </button>
      </div>
    </>
  );
}

function statusLabel(s: string): string {
  switch (s) {
    case 'will_grant':
      return 'Will add';
    case 'will_queue':
      return 'New login';
    case 'already_member':
      return 'Already a member';
    case 'already_queued':
      return 'Login already waiting';
    case 'parse_error':
      return 'Error';
    default:
      return s;
  }
}

function statusBadgeClass(s: string): string {
  const base = 'inline-flex items-center px-2 py-0.5 text-[10px] font-semibold rounded-full ';
  switch (s) {
    case 'will_grant':
      return base + 'bg-sage-100 text-sage-700';
    case 'will_queue':
      return base + 'bg-gold-soft text-caution-deep';
    case 'already_member':
    case 'already_queued':
      return base + 'bg-sand text-ink-soft';
    case 'parse_error':
      return base + 'bg-pepper-soft text-pepper-deep';
    default:
      return base + 'bg-sand text-ink-soft';
  }
}
